import React, { useState, useEffect, useRef } from "react";
import { useAppNavigate } from "utils/router";
import { Eye, EyeOff, Info, BookOpen, CalendarIcon, X, ChevronDown, CheckCircle, Check, Zap, CreditCard, UserPlus, Database, Loader2, AlertTriangle, Plug, Copy, ExternalLink, ArrowRight, Pencil, Lock } from "lucide-react";
import { GroupIdMappingModal } from "../Stripe/components/GroupIdMappingModal";
import { ChargebeeSetupGuide } from "./components/ChargebeeSetupGuide";
import { TestSyncResults } from "./components/TestSyncResults";
import { InviteTeamModal } from "../../Common/InviteTeamModal";
import { useGetEnvironmentsForProductLazyQuery, useCreateEnvironmentCaMutation, EnvironmentType } from "store/graphql/codegen/auto-gen/configurator";
import { configuratorClient, appPricingClient, revenueClient } from "store/graphql/apollo/clients";
import { TeamId } from "store/redux/reducers/team/teamConstants";
import ChargebeeIcon from '../../../../assets/images/Chargebee_ideEpxhfdE_1.svg';
import { useTestChargebeeConnectionLazyQuery } from '../../../../store/graphql/codegen/auto-gen/app-pricing'
import { useCreateStripeIntegrationMutation as useCreateChargebeeIntegrationMutation, useCreateAirbyteIntegrationMutation, IntegrationType, StripeEnvironmentType as ChargebeeEnvironmentType, useGetSyncStatusLazyQuery, DataSource, useGetStripeAccountDetailsLazyQuery as useGetChargebeeAccountDetailsLazyQuery, useUpdateAirbyteSourceMutation, useTriggerSyncMutation, useResetAndResyncAirbyteDataMutation } from '../../../../store/graphql/codegen/auto-gen/revenue-report'
import { useDemoMode } from "../../../../context/DemoModeContext";
import { getProductId, getUser, getProductName, getEnvironmentDisplayName, getChargebeeFormData, setChargebeeFormData, removeChargebeeFormData } from "utils/localStorage";
import {
    simulateTestStripeConnection as simulateTestChargebeeConnection,
    simulateCreateStripeIntegration as simulateCreateChargebeeIntegration,
    DEMO_ENVIRONMENT,
    DEMO_TEST_SCENARIOS
} from '../Stripe/components/stripeDemoHelpers';
import { validateEmail } from '../utils/utils'
import { EmailManager } from "../RevenueAnalytics";
import { useLocation } from "react-router-dom";
import { DemoStripeModal as DemoChargebeeModal } from '../Stripe/components/DemoStripeModal'
import { FixInstructionsModal, FixInstructionsContent } from '../Stripe/components/FixInstructionsModal'

type SyncStatusEntry = {
    dataSource?: string | null;
    status?: string | null;
    syncType?: string | null;
    lastUpdatedAt?: string | null;
    startTime?: string | null;
};

function pickChargebeeDataSyncStatus(entries: SyncStatusEntry[] | null | undefined) {
    if (!entries?.length) return null;
    const chargebeeSync = entries.filter((e) => {
        const ds = (e.dataSource || '').toString().toUpperCase();
        const st = (e.syncType || '').toLowerCase();
        return ds === 'CHARGEBEE' && st === 'sync';
    });
    if (chargebeeSync.length === 0) return null;
    return [...chargebeeSync].sort((a, b) => {
        const ta = a.startTime ? new Date(a.startTime).getTime() : 0;
        const tb = b.startTime ? new Date(b.startTime).getTime() : 0;
        return tb - ta;
    })[0];
}

interface ChargebeeIntegrationProps {
    sourceConfigurations: Record<string, any>;
    updateSourceConfiguration: (sourceId: string, config: any) => void;
    onComplete?: (config: any) => void;
    loadedChargebeeData?: any;
    selectedSources: string;
    config: Record<string, any>;
    setConfig: (config: any) => void;
    onStepComplete: (value: boolean, overrideValues?: any) => void;
    emailManager: EmailManager;
    onAutoSave: () => void;
    cleanOAuthParams: () => void;
    openSetupGuideRef?: React.MutableRefObject<(() => void) | null>;
    onOpenIntegrationsList: () => void;
    onTrackCompletion?: () => void;
}

interface FormData {
    sourceName: string;
    subdomain: string;
    apiKey: string;
    secretKey: string;
    productCatalog: '1.0' | '2.0';
    dataRetentionLocation: string;
    syncStartDate: Date;
    groupIdMapping: boolean;
    environmentId: string;
    environmentName: string;
    chargebeeEnvironment: string;
    chargebeeFeePerTransaction: number;
    paymentGatewayFees: number;
    accountId: string;
}

interface StepConfig {
    id: string;
    number: number;
    title: string;
    description: string;
    subtitle?: string;
    isCompleted: boolean;
    isActive: boolean;
    required: boolean;
}

interface NewEnvironmentForm {
    displayName: string;
    environmentType: EnvironmentType;
}

interface TestingStep {
    id: string;
    title: string;
    loadingText: string;
    successText: string;
    errorText: string;
    status: 'pending' | 'loading' | 'success' | 'error';
    missingEntities?: string[];
    selectedIdentifierKey?: string;
    customerMetaDataKeys?: string[];
}

const getDefaultSyncDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 12);
    return date;
};

const CHARGEBEE_TABLES = {
    available: [
        "addon", "customer", "subscription", "invoice", "item_price", "plan", "item", "transaction", "event",
        "credit_note", "payment_source", "subscription_entitlement", "item_entitlement", "coupon", "promotional_credit",
        "unbilled_charge", "order", "gift", "quote", "attached_item", "differential_price", "virtual_bank_account"
    ],
    willSync: [
        "addon",
        "customer",
        "subscription",
        "invoice",
        "item_price",
        "plan",
        "item",
        "transaction",
        "event"
    ]
};

const normalizeKey = (key: string): string => {
  return key.toLowerCase().replace(/[_\s-]/g, '');
};

const findGroupIdKey = (keys: string[]): string | null => {
  const normalizedGroupId = normalizeKey('group_id');
  return keys.find(key => normalizeKey(key) === normalizedGroupId) || null;
};

export const ChargebeeIntegration: React.FC<ChargebeeIntegrationProps> = ({
    sourceConfigurations,
    updateSourceConfiguration,
    onComplete,
    loadedChargebeeData,
    selectedSources,
    config,
    setConfig,
    onStepComplete,
    emailManager,
    onAutoSave,
    cleanOAuthParams,
    openSetupGuideRef,
    onOpenIntegrationsList,
    onTrackCompletion,
}) => {

    const [formData, setFormData] = useState<FormData>({
        sourceName: "Chargebee",
        subdomain: "",
        apiKey: "",
        secretKey: "",
        productCatalog: "2.0",
        dataRetentionLocation: "",
        syncStartDate: getDefaultSyncDate(),
        groupIdMapping: false,
        environmentId: "",
        environmentName: "",
        chargebeeEnvironment: "",
        chargebeeFeePerTransaction: 0,
        paymentGatewayFees: 0,
        accountId: "",
    });
    const { isDemoMode } = useDemoMode()
    const [showSecretKey, setShowSecretKey] = useState(false);
    const [isSetupGuideOpen, setIsSetupGuideOpen] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedOption, setSelectedOption] = useState<"proceed" | "pause" | null>(null);
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [errors, setErrors] = useState<Partial<FormData>>({});
    const [activeStep, setActiveStep] = useState<string>('environment');
    const [isManualStepChange, setIsManualStepChange] = useState(false);
    const [showEnvironmentDropdown, setShowEnvironmentDropdown] = useState(false);
    const [showChargebeeEnvironmentDropdown, setShowChargebeeEnvironmentDropdown] = useState(false);
    const [showProductCatalogDropdown, setShowProductCatalogDropdown] = useState(false);
    const [showNewEnvironmentForm, setShowNewEnvironmentForm] = useState(false);
    const [newEnvironmentForm, setNewEnvironmentForm] = useState<NewEnvironmentForm>({
        displayName: '',
        environmentType: EnvironmentType.NonProduction
    });
    const [isTesting, setIsTesting] = useState(false);
    const setupGuideStep3Ref = useRef<HTMLDivElement>(null);
    const [selectedAccountKey, setSelectedAccountKey] = useState<string>('group_id');
    const [showAccountKeyDropdown, setShowAccountKeyDropdown] = useState(false);
    const [customerMetaDataKeys, setCustomerMetaDataKeys] = useState<string[]>([]);
    const [groupIdFound, setGroupIdFound] = useState<boolean>(false);
    const [testingSteps, setTestingSteps] = useState<TestingStep[]>([
        {
            id: 'connection',
            title: 'Connection',
            loadingText: 'Establishing Connection',
            successText: 'Connected',
            errorText: 'Connection Error',
            status: 'pending'
        },
        {
            id: 'data',
            title: 'Data Retrieval',
            loadingText: 'Retrieving Data Tables',
            successText: 'Data Retrieved',
            errorText: 'Unable to Retrieve Data',
            status: 'pending'
        },
        {
            id: 'schema',
            title: 'Schema Verification',
            loadingText: 'Verifying Schema',
            successText: 'Schema Verified',
            errorText: 'Schema Error Detected',
            status: 'pending'
        }
    ]);
    const ingestionLoadingStepsDefault = [
        {
            id: 'create-source',
            title: 'Creating Source',
            loadingText: 'Creating Source...',
            successText: 'Source Created',
            errorText: 'Failed to Create Source',
            status: 'pending'
        },
        {
            id: 'make-connection',
            title: 'Making Connection',
            loadingText: 'Making Connection...',
            successText: 'Connection Established',
            errorText: 'Connection Error',
            status: 'pending'
        },
        {
            id: 'sync-data',
            title: 'Syncing Data',
            loadingText: 'Starting Data Sync...',
            successText: 'Data Sync Started',
            errorText: 'Sync Error',
            status: 'pending'
        }
    ];

    const [testCompleted, setTestCompleted] = useState<boolean>(false);
    const [dotCount, setDotCount] = useState(1);
    const [isTablesExpanded, setIsTablesExpanded] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{
        siteType?: string;
        siteName?: string;
        productCatalog?: string;
        credentials?: string;
        entitySync?: Array<{ entityName: string; errorMessage?: string }>;
    } | null>(null);
    const [credentialsErrorDisplayMessage, setCredentialsErrorDisplayMessage] = useState<string | null>(null);

    const navigate = useAppNavigate();
    const location = useLocation();

    const [selectedTeam, setSelectedTeam] = useState<TeamId>();
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [isStep3Incomplete, setIsStep3Incomplete] = useState<boolean>(false);
    const [showFixInstructionsModal, setShowFixInstructionsModal] = useState(false);

    const [ingestionResult, setIngestionResult] = useState<{
        sourceId: string;
        connectionId: string;
        syncStartedAt: string;
        syncStatus: string;
        jobId: number;
    } | null>(null);
    const [ingestionError, setIngestionError] = useState<string | null>(null);

    const step1NumberRef = useRef<HTMLDivElement>(null);
    const step2NumberRef = useRef<HTMLDivElement>(null);
    const step3NumberRef = useRef<HTMLDivElement>(null);
    const step4NumberRef = useRef<HTMLDivElement>(null);

    const step2ContainerRef = useRef<HTMLDivElement>(null);
    const step3ContainerRef = useRef<HTMLDivElement>(null);
    const step4ContainerRef = useRef<HTMLDivElement>(null);

    const substep2_2Ref = useRef<HTMLDivElement>(null);
    const substepFromUrl = useRef<string | null>(null);
    const userChosenStepRef = useRef<string | null>(null);

    const localRef = useRef<boolean>(false)
    const didRestoreFromSavedConfigRef = useRef<string | boolean>(false)

    const [step3Completed, setStep3Completed] = useState<boolean>(
        !!sourceConfigurations.chargebee?.step3Completed
    );
    const [step4Completed, setStep4Completed] = useState<boolean>(
        !!sourceConfigurations.chargebee?.step4Completed
    );
    const [step4WasEverCompleted, setStep4WasEverCompleted] = useState<boolean>(
        !!sourceConfigurations.chargebee?.step4WasEverCompleted ||
        !!loadedChargebeeData?.step4WasEverCompleted ||
        !!sourceConfigurations.chargebee?.step4CompletedAt ||
        !!loadedChargebeeData?.step4CompletedAt
    );
    const [showIngestionLoadingSteps, setShowIngestionLoadingSteps] = useState(false);
    const [ingestionLoadingSteps, setIngestionLoadingSteps] = useState(ingestionLoadingStepsDefault);
    const [ingestionStepIndex, setIngestionStepIndex] = useState(0);
    const [expanded, setExpanded] = useState(false);

    const [syncStatus, setSyncStatus] = useState<'pending' | 'running' | 'succeeded' | 'failed'>('pending');
    const [isSyncPolling, setIsSyncPolling] = useState(false);
    const [accountIdLoading, setAccountIdLoading] = useState<boolean>(false);
    
    
    const [isEditingSourceName, setIsEditingSourceName] = useState(false);
    const [isEditingSubdomain, setIsEditingSubdomain] = useState(false);
    const [isEditingApiKey, setIsEditingApiKey] = useState(false);
    const [isEditingProductCatalog, setIsEditingProductCatalog] = useState(false);
    const [isEditingSyncDate, setIsEditingSyncDate] = useState(false);
    const [basicConfigSavedThisSession, setBasicConfigSavedThisSession] = useState(false);
    const isBasicConfigStepCompleted = !!sourceConfigurations.chargebee?.step2Completed || basicConfigSavedThisSession;
    const [isUpdatingSource, setIsUpdatingSource] = useState(false);
    const [updateSourceError, setUpdateSourceError] = useState<string | null>(null);
    const [originalSourceName, setOriginalSourceName] = useState<string>('');
    const [originalSubdomain, setOriginalSubdomain] = useState<string>('');
    const [originalApiKey, setOriginalApiKey] = useState<string>('');
    const [originalProductCatalog, setOriginalProductCatalog] = useState<'1.0' | '2.0' | ''>('');
    const [originalSyncDate, setOriginalSyncDate] = useState<Date | null>(null);
    const [isSetupIncomplete, setIsSetupIncomplete] = useState(false);

    type PostSaveFlowMode = 'none' | 'apiKeyOnly' | 'syncDateOnly' | 'both';
    const [postSaveFlowMode, setPostSaveFlowMode] = useState<PostSaveFlowMode>('none');
    const [isConfigEditPendingBigSave, setIsConfigEditPendingBigSave] = useState(false);
    const [pendingApiKeyChange, setPendingApiKeyChange] = useState(
        !!(sourceConfigurations.chargebee as any)?.pendingApiKeyChange || 
        !!(loadedChargebeeData as any)?.pendingApiKeyChange
    );
    const [pendingSyncDateChange, setPendingSyncDateChange] = useState(
        !!(sourceConfigurations.chargebee as any)?.pendingSyncDateChange || 
        !!(loadedChargebeeData as any)?.pendingSyncDateChange
    );
    const [resyncStarted, setResyncStarted] = useState(false);
    const [requiresStep3CompletionForResync, setRequiresStep3CompletionForResync] = useState(false);
    const [lastSyncedDate, setLastSyncedDate] = useState<string | null>(null);
    const [showDemoModal, setShowDemoModal] = useState(false);

    const [getEnvironmentsForProduct, { data: environmentsData, loading: environmentsLoading }] = useGetEnvironmentsForProductLazyQuery({
        client: configuratorClient,
        fetchPolicy: "no-cache"
    });

    const [createEnvironmentCaMutation, { loading: createEnvironmentLoading }] = useCreateEnvironmentCaMutation({
        client: configuratorClient,
        fetchPolicy: "no-cache"
    });

    const [TestChargebeeConnection] = useTestChargebeeConnectionLazyQuery({
        client: appPricingClient,
        fetchPolicy: "no-cache"
    });

    const [GetChargebeeAccountDetails] = useGetChargebeeAccountDetailsLazyQuery({
        client: appPricingClient,
        fetchPolicy: "no-cache"
    });

    const [createAirbyteIntegration, { loading: createIntegrationLoading }] = useCreateAirbyteIntegrationMutation({
        client: revenueClient,
        fetchPolicy: "no-cache"
    });

    const [updateAirbyteSource, { loading: updateAirbyteSourceLoading }] = useUpdateAirbyteSourceMutation({
        client: revenueClient,
        fetchPolicy: "no-cache"
    });

    const [triggerSync, { loading: triggerSyncLoading }] = useTriggerSyncMutation({
        client: revenueClient,
        fetchPolicy: "no-cache"
    });

    const [resetAndResyncAirbyteData, { loading: resetAndResyncLoading }] = useResetAndResyncAirbyteDataMutation({
        client: revenueClient,
        fetchPolicy: "no-cache"
    });

    const [GetSyncStatus] = useGetSyncStatusLazyQuery({
        client: revenueClient,
        fetchPolicy: "no-cache"
    });

    useEffect(() => {
        const saved = sourceConfigurations?.chargebee;
        if (!saved || Object.keys(saved).length === 0) return;

        const savedDataKey = JSON.stringify({
            subdomain: saved.subdomain,
            apiKey: saved.apiKey?.substring(0, 10), 
            syncStartDate: saved.syncStartDate,
            step2Completed: saved.step2Completed,
            step3Completed: saved.step3Completed,
            step4Completed: saved.step4Completed,
            ingestionResult: saved.ingestionResult?.sourceId
        });
        
        if (didRestoreFromSavedConfigRef.current === savedDataKey) return;
        didRestoreFromSavedConfigRef.current = savedDataKey;

        setFormData(prev => ({
            ...prev,
            sourceName: saved.sourceName ?? prev.sourceName,
            subdomain: saved.subdomain ?? prev.subdomain,
            apiKey: saved.apiKey ?? prev.apiKey,
            secretKey: saved.secretKey ?? prev.secretKey,
            productCatalog: saved.productCatalog ?? prev.productCatalog,
            dataRetentionLocation: saved.dataRetentionLocation ?? prev.dataRetentionLocation,
            syncStartDate: saved.syncStartDate ? new Date(saved.syncStartDate) : prev.syncStartDate,
            groupIdMapping: saved.groupIdMapping ?? prev.groupIdMapping,
            environmentId: saved.environmentId ?? prev.environmentId,
            environmentName: saved.environmentName ?? prev.environmentName,
            chargebeeEnvironment: saved.chargebeeEnvironment ?? prev.chargebeeEnvironment,
            chargebeeFeePerTransaction: saved.chargebeeFeePerTransaction ?? prev.chargebeeFeePerTransaction,
            paymentGatewayFees: saved.paymentGatewayFees ?? prev.paymentGatewayFees,
            accountId: saved.accountId ?? prev.accountId,
        }));
        
        
        setIsEditingSourceName(false);
        setIsEditingSubdomain(false);
        setIsEditingApiKey(false);
        setIsEditingProductCatalog(false);
        setIsEditingSyncDate(false);
        setOriginalSourceName('');
        setOriginalSubdomain('');
        setOriginalApiKey('');
        setOriginalProductCatalog('');
        setOriginalSyncDate(null);

        
        if (Array.isArray(saved.testingSteps)) {
            setTestingSteps(saved.testingSteps);
        }
        if (Array.isArray(saved.customerMetaDataKeys)) {
            setCustomerMetaDataKeys(saved.customerMetaDataKeys);
        }
        if (typeof saved.groupIdFound === 'boolean') {
            setGroupIdFound(saved.groupIdFound);
        }
        if (typeof saved.testCompleted === 'boolean') {
            setTestCompleted(saved.testCompleted);
        }
        if (typeof saved.isStep3Incomplete === 'boolean') {
            setIsStep3Incomplete(saved.isStep3Incomplete);
        }
        if (typeof saved.step3Completed === 'boolean') {
            setStep3Completed(saved.step3Completed);
        }
        if (typeof saved.step4Completed === 'boolean') {
            setStep4Completed(prev => {
                if (saved.step4Completed === true) return true;
                if (
                    saved.step4Completed === false &&
                    postSaveFlowMode === 'apiKeyOnly' &&
                    resyncStarted &&
                    prev
                ) {
                    return true;
                }
                return saved.step4Completed;
            });
        }
        if (typeof saved.step4WasEverCompleted === 'boolean') {
            setStep4WasEverCompleted(saved.step4WasEverCompleted);
        }
        if (saved.ingestionResult) {
            setIngestionResult(saved.ingestionResult);
            
            if (saved.ingestionResult.syncStartedAt) {
                setLastSyncedDate(saved.ingestionResult.syncStartedAt);
            }
            
            
            const statusToUse = saved.ingestionResult.syncStatus || (saved as any).syncStatus;
            if (statusToUse) {
                setSyncStatus(statusToUse as any);
                
                if (statusToUse === 'running' || statusToUse === 'pending') {
                    setIsSyncPolling(true);
                }
            }
        } else if ((saved as any).syncStatus) {
            
            setSyncStatus((saved as any).syncStatus as any);
            if ((saved as any).syncStatus === 'running' || (saved as any).syncStatus === 'pending') {
                setIsSyncPolling(true);
            }
        }
        if (typeof saved.isSetupIncomplete === 'boolean') {
            setIsSetupIncomplete(saved.isSetupIncomplete);
        }

        // Load flags from config - only set to true, never back to false
        // (flags are only set to false on cancel or after successful resync)
        if ((saved as any).pendingApiKeyChange === true) {
            setPendingApiKeyChange(true);
        }

        if ((saved as any).pendingSyncDateChange === true) {
            setPendingSyncDateChange(true);
        }

        if ((saved as any).pendingApiKeyChange || (saved as any).pendingSyncDateChange) {
            const mode: PostSaveFlowMode =
                (saved as any).pendingApiKeyChange && (saved as any).pendingSyncDateChange
                    ? 'both'
                    : (saved as any).pendingSyncDateChange
                      ? 'syncDateOnly'
                      : (saved as any).pendingApiKeyChange
                        ? 'apiKeyOnly'
                        : 'none';
            setPostSaveFlowMode(mode);
        }

        
        if (typeof saved.selectedIdentifierKey === 'string' && saved.selectedIdentifierKey) {
            setSelectedAccountKey(saved.selectedIdentifierKey);
        } else if (Array.isArray(saved.customerMetaDataKeys) && saved.customerMetaDataKeys.length > 0) {
            setSelectedAccountKey(saved.customerMetaDataKeys[0]);
        }

        
        
        if (!isManualStepChange) {
            if (saved.isStep3Incomplete) {
                setActiveStep('revenue-correlation');
            } else if (saved.step4Completed) {
                setActiveStep('pause-fix');
            } else if (saved.step3Completed) {
                setActiveStep('revenue-correlation');
            } else if (saved.isSetupIncomplete) {
                setActiveStep('basic-config');
            } else if (saved.step2Completed) {
                setActiveStep('revenue-correlation');
            } else if (saved.step1Completed) {
                setActiveStep('basic-config');
            }
        }
    }, [sourceConfigurations, isManualStepChange, postSaveFlowMode, resyncStarted]);

    useEffect(() => {
        if (step4Completed && !step4WasEverCompleted) {
            setStep4WasEverCompleted(true);
        }
    }, [step4Completed, step4WasEverCompleted]);

    useEffect(() => {
        const savedValue = sourceConfigurations.chargebee?.step4WasEverCompleted || 
                          loadedChargebeeData?.step4WasEverCompleted ||
                          sourceConfigurations.chargebee?.step4CompletedAt ||
                          loadedChargebeeData?.step4CompletedAt;
        if (savedValue && !step4WasEverCompleted) {
            setStep4WasEverCompleted(true);
        }
    }, [
        sourceConfigurations.chargebee?.step4WasEverCompleted, 
        loadedChargebeeData?.step4WasEverCompleted,
        sourceConfigurations.chargebee?.step4CompletedAt,
        loadedChargebeeData?.step4CompletedAt,
        step4WasEverCompleted
    ]);

    const fetchEnvironments = async () => {
        try {
            await getEnvironmentsForProduct({
                variables: {
                    input: {
                        productId: getProductId() as string
                    }
                }
            });
        } catch (error) {
            console.error('Failed to fetch environments:', error);
        }
    };

    const handleDemoChargebeeConnect = () => {
        const demoAccountId = `acct_demo_${Math.random().toString(36).substring(2, 15)}`;

        setFormData(prev => ({
            ...prev,
            accountId: demoAccountId
        }));

        setShowDemoModal(false);
    };


    const areAllStepsSuccessful = () => {
        return testingSteps.every(step => step.status === 'success');
    };

    const useCopyToClipboard = (timeout = 2000) => {
        const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});

        const copy = async (text: string, id: string) => {
            await navigator.clipboard.writeText(text);
            setCopiedStates(prev => ({ ...prev, [id]: true }));

            setTimeout(() => {
                setCopiedStates(prev => ({ ...prev, [id]: false }));
            }, timeout);
        };

        return { copiedStates, copy };
    };

    const handleSaveAndProceed = async () => {
        if (isConfigEditPendingBigSave || pendingApiKeyChange || pendingSyncDateChange) {
            setIsUpdatingSource(true);
            setUpdateSourceError(null);
            
            try {
                await updateAirbyteSource({
                    variables: {
                        input: {
                            environmentId: formData.environmentId,
                            sourceType: IntegrationType.Chargebee,
                            startDate:
                                formData.syncStartDate instanceof Date
                                    ? formData.syncStartDate.toISOString().split('.')[0] + 'Z'
                                    : new Date().toISOString().split('.')[0] + 'Z',
                            apiKey: formData.apiKey,
                            shouldReset: false
                        }
                    }
                });
                
                setIsConfigEditPendingBigSave(false);
                
            } catch (e) {
                console.error('Failed to update Chargebee source:', e);
                setUpdateSourceError('Something went wrong, try again');
                setIsUpdatingSource(false);
                return; 
            }
            
            setIsUpdatingSource(false);
        }
        
        setStep3Completed(true);
        setIsStep3Incomplete(false);
        
        setBasicConfigSavedThisSession(true);
        
        userChosenStepRef.current = 'pause-fix';
        setActiveStep('pause-fix');
        saveConfiguration(false);
        onAutoSave();
        setTimeout(() => {
            step4NumberRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };

    const handleInviteAndProceedWithMetadata = async () => {
        if (isConfigEditPendingBigSave || pendingApiKeyChange || pendingSyncDateChange) {
            setIsUpdatingSource(true);
            setUpdateSourceError(null);
            
            try {
                await updateAirbyteSource({
                    variables: {
                        input: {
                            environmentId: formData.environmentId,
                            sourceType: IntegrationType.Chargebee,
                            startDate:
                                formData.syncStartDate instanceof Date
                                    ? formData.syncStartDate.toISOString().split('.')[0] + 'Z'
                                    : new Date().toISOString().split('.')[0] + 'Z',
                            apiKey: formData.apiKey,
                            shouldReset: false
                        }
                    }
                });
                
                setIsConfigEditPendingBigSave(false);
                
            } catch (e) {
                console.error('Failed to update Chargebee source:', e);
                setUpdateSourceError('Something went wrong, try again');
                setIsUpdatingSource(false);
                return; 
            }
            
            setIsUpdatingSource(false);
        }
        
        setIsStep3Incomplete(true);
        setStep3Completed(true);
        
        setBasicConfigSavedThisSession(true);
        
        userChosenStepRef.current = 'pause-fix';
        setActiveStep('pause-fix');
        saveConfiguration(true);
        setSelectedTeam(TeamId.REVOPS_TEAMS);
        setShowInviteModal(true);
        onAutoSave();
        setTimeout(() => {
            step4NumberRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };


    const { copiedStates, copy } = useCopyToClipboard()

    const testIntegration = async (scenario: keyof typeof DEMO_TEST_SCENARIOS = 'SUCCESS'): Promise<any> => {
        if (isDemoMode) {
            const result = await simulateTestChargebeeConnection(scenario);

            if (result.customerMetaDataKeys) {
                setCustomerMetaDataKeys(result.customerMetaDataKeys);
            } else {
                setCustomerMetaDataKeys([]);
            }
            setGroupIdFound(result.groupIdFound);

            if (result.customerMetaDataKeys && result.customerMetaDataKeys.includes('group_id')) {
                setSelectedAccountKey('group_id');
            } else if (result.customerMetaDataKeys && result.customerMetaDataKeys.length > 0) {
                setSelectedAccountKey(result.customerMetaDataKeys[0]);
            }

            return result;
        }

        try {
            const result = await TestChargebeeConnection({
                variables: {
                    input: {
                        siteType: formData.chargebeeEnvironment || "test",
                        siteName: formData.subdomain || "",
                        apiKey: formData.apiKey || "",
                        productCatalogVersion: formData.productCatalog || "2.0"
                    }
                }
            });

            const response = result.data?.testChargebeeConnection;
            if (!response) {
                throw new Error('CHARGEBEE_TEST_CONNECTION_NO_RESPONSE');
            }

            
            const errors: typeof validationErrors = {};
            if (response?.validSiteTypeMessage) {
                errors.siteType = response.validSiteTypeMessage;
            }
            if (response?.validSiteNameMessage) {
                errors.siteName = response.validSiteNameMessage;
            }
            if (response?.validProductCatalogMessage) {
                errors.productCatalog = response.validProductCatalogMessage;
            }
            if (response?.credentialsMessage) {
                errors.credentials = response.credentialsMessage;
            }

            
            if (Object.keys(errors).length > 0) {
                setValidationErrors(errors);
            } else {
                setValidationErrors(null);
            }

            if (response?.credentialsValid && response?.validSiteType && 
                response?.validSiteNameMatchesType && response?.validProductCatalogVersion) {
                if (response?.customerMetaDataKeys) {
                    setCustomerMetaDataKeys(response.customerMetaDataKeys);
                } else {
                    setCustomerMetaDataKeys([]);
                }
                if (response?.customerMetaDataKeys && response.customerMetaDataKeys.length > 0) {
                    const groupIdKey = findGroupIdKey(response.customerMetaDataKeys);
                    if (groupIdKey) {
                        setSelectedAccountKey(groupIdKey);
                        setGroupIdFound(true);
                    } else {
                        setSelectedAccountKey(response.customerMetaDataKeys[0]);
                        setGroupIdFound(false);
                    }
                } else {
                    setGroupIdFound(false);
                }
            } else {
                setCustomerMetaDataKeys([]);
                setGroupIdFound(false);
            }

            return response;
        } catch (error) {
            console.error('Test connection error:', error);
            throw error;
        }
    };

    const stepsConfig: StepConfig[] = [
        {
            id: 'environment',
            number: 1,
            title: 'Map Environments',
            description: 'Select the Chargebee site that will map with current App environment and will be used when establishing an connection.',
            subtitle: 'Select the Chargebee site to connect with Current App environment',
            isCompleted: !!(formData.environmentId && formData.chargebeeEnvironment),
            isActive: activeStep === 'environment',
            required: true
        },
        {
            id: 'basic-config',
            number: 2,
            title: 'Basic Configuration',
            description: 'Configure your Chargebee connection details',
            isCompleted: !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate),
            isActive: activeStep === 'basic-config',
            required: true
        },
        {
            id: 'revenue-correlation',
            number: 3,
            title: 'Test Your Chargebee Connection',
            description: 'Test your Chargebee credentials before starting Data Ingestion',
            isCompleted: step3Completed || areAllStepsSuccessful(),
            isActive: activeStep === 'revenue-correlation',
            required: true
        },
        {
            id: 'pause-fix',
            number: 4,
            title:
                (postSaveFlowMode === 'apiKeyOnly' ||
                postSaveFlowMode === 'both' ||
                postSaveFlowMode === 'syncDateOnly') &&
                step4WasEverCompleted
                    ? 'Resync Chargebee data'
                    : 'Start Ingestion',
            description:
                (postSaveFlowMode === 'apiKeyOnly' ||
                postSaveFlowMode === 'both' ||
                postSaveFlowMode === 'syncDateOnly') &&
                step4WasEverCompleted
                    ? 'Start a new sync with modified configurations'
                    : 'Finalize your Chargebee integration',
            isCompleted: step4Completed || false,
            isActive: activeStep === 'pause-fix',
            required: true
        }
    ];

    const handleUpdateNotificationEmails = () => {
        const updatedConfig = {
            notificationEmails: {
                userEmail: getUser()?.email,
                additionalEmails: emailManager.state.emails
            }
        };

        updateSourceConfiguration('chargebee', updatedConfig);
        setConfig((prev: any) => ({
            ...prev,
            ...updatedConfig
        }));
        emailManager.resetUpdateButton();
    };

    
    useEffect(() => {
        if (openSetupGuideRef) {
            openSetupGuideRef.current = () => setIsSetupGuideOpen(true);
        }
        return () => {
            if (openSetupGuideRef) {
                openSetupGuideRef.current = null;
            }
        };
    }, [openSetupGuideRef]);

    
    useEffect(() => {
        const isResyncFlow =
            (postSaveFlowMode === 'apiKeyOnly' ||
            postSaveFlowMode === 'both' ||
            postSaveFlowMode === 'syncDateOnly') &&
            sourceConfigurations.chargebee?.step4Completed;

        const shouldLoadStatus =
            activeStep === 'pause-fix' &&
            !!formData.environmentId &&
            !!ingestionResult &&
            (!isResyncFlow || resyncStarted);

        if (shouldLoadStatus) {
            
            const timeoutId = setTimeout(() => {
                const loadSyncStatus = async () => {
                    try {
                        const result = await GetSyncStatus({
                            variables: {
                                input: {
                                    environmentId: formData.environmentId,
                                    dataSource: [DataSource.Chargebee]
                                }
                            }
                        });

                        const syncStatusData = pickChargebeeDataSyncStatus(
                            result.data?.getSyncStatus?.syncStatus as SyncStatusEntry[] | undefined
                        );
                        if (syncStatusData) {
                            const status = syncStatusData.status;
                            setSyncStatus(status as any);
                            
                            
                            if (syncStatusData.lastUpdatedAt) {
                                setLastSyncedDate(syncStatusData.lastUpdatedAt);
                            } else if (ingestionResult.syncStartedAt && !lastSyncedDate) {
                                setLastSyncedDate(ingestionResult.syncStartedAt);
                            }

                            
                            if (status === 'running' || status === 'pending') {
                                setIsSyncPolling(true);
                            }
                        }
                    } catch (error) {
                        console.error('Error loading sync status:', error);
                    }
                };

                loadSyncStatus();
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [activeStep, ingestionResult, formData.environmentId, GetSyncStatus, postSaveFlowMode, resyncStarted]);

    useEffect(() => {
        const isResyncFlow =
            (postSaveFlowMode === 'apiKeyOnly' ||
            postSaveFlowMode === 'both' ||
            postSaveFlowMode === 'syncDateOnly') &&
            sourceConfigurations.chargebee?.step4Completed;
        if (isResyncFlow && !resyncStarted && !triggerSyncLoading && !resetAndResyncLoading) {
            setResyncStarted(false);
            setIsSyncPolling(false);
            setSyncStatus('pending');
            setLastSyncedDate(null);
        }
    }, [postSaveFlowMode, sourceConfigurations.chargebee?.step4Completed, resyncStarted, triggerSyncLoading, resetAndResyncLoading]);

    useEffect(() => {
        setRequiresStep3CompletionForResync(
            postSaveFlowMode === 'apiKeyOnly' ||
            postSaveFlowMode === 'both' ||
            postSaveFlowMode === 'syncDateOnly'
        );
    }, [postSaveFlowMode]);

    useEffect(() => {
        if (!isSyncPolling || !formData.environmentId) return;

        const isResyncFlow =
            (postSaveFlowMode === 'apiKeyOnly' ||
            postSaveFlowMode === 'both' ||
            postSaveFlowMode === 'syncDateOnly') &&
            sourceConfigurations.chargebee?.step4Completed;
        if (isResyncFlow && !resyncStarted) return;

        let pollInterval: NodeJS.Timeout;
        let maxAttempts = 40;
        let attempts = 0;

        const pollSyncStatus = async () => {
            try {
                attempts++;

                if (attempts > maxAttempts) {
                    setSyncStatus('failed');
                    setIsSyncPolling(false);
                    clearInterval(pollInterval);
                    return;
                }

                const result = await GetSyncStatus({
                    variables: {
                        input: {
                            environmentId: formData.environmentId,
                            dataSource: [DataSource.Chargebee]
                        }
                    }
                });

                const syncStatusData = pickChargebeeDataSyncStatus(
                    result.data?.getSyncStatus?.syncStatus as SyncStatusEntry[] | undefined
                );
                if (syncStatusData) {
                    const status = syncStatusData.status;
                    setSyncStatus(status as any);
                    
                    
                    if (syncStatusData.lastUpdatedAt) {
                        setLastSyncedDate(syncStatusData.lastUpdatedAt);
                    }

                    if (status === 'succeeded' || status === 'failed') {
                        setIsSyncPolling(false);
                        clearInterval(pollInterval);
                    }
                }
            } catch (error) {
                console.error('Error polling sync status:', error);
                setSyncStatus('failed');
                setIsSyncPolling(false);
                clearInterval(pollInterval);
            }
        };

        pollSyncStatus();
        pollInterval = setInterval(pollSyncStatus, 30000);

        return () => {
            clearInterval(pollInterval);
        };
    }, [isSyncPolling, formData.environmentId, GetSyncStatus, postSaveFlowMode, resyncStarted]);

    useEffect(() => {
        if (!showIngestionLoadingSteps) return;

        if (ingestionError) {
            setIngestionLoadingSteps(prev =>
                prev.map(step => ({
                    ...step,
                    status: 'error'
                }))
            );
            return;
        }

        if (ingestionStepIndex < ingestionLoadingSteps.length - 1) {
            setIngestionLoadingSteps(prev =>
                prev.map((step, idx) =>
                    idx === ingestionStepIndex ? { ...step, status: 'loading' } : step
                )
            );

            const timer = setTimeout(() => {
                setIngestionLoadingSteps(prev =>
                    prev.map((step, idx) =>
                        idx === ingestionStepIndex ? { ...step, status: 'success' } : step
                    )
                );
                setIngestionStepIndex(i => i + 1);
            }, 3000);

            return () => clearTimeout(timer);
        }

        if (ingestionStepIndex === ingestionLoadingSteps.length - 1) {
            setIngestionLoadingSteps(prev =>
                prev.map((step, idx) =>
                    idx === ingestionStepIndex
                        ? { ...step, status: createIntegrationLoading ? 'loading' : 'success' }
                        : step
                )
            );
        }
    }, [showIngestionLoadingSteps, ingestionStepIndex, createIntegrationLoading, ingestionLoadingSteps.length, ingestionError]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const stateParam = urlParams.get('state');

        const hasExistingAccountId =
            formData.accountId !== '' ||
            loadedChargebeeData?.formData?.accountId ||
            config?.accountId ||
            sourceConfigurations.chargebee?.accountId;

        if (hasExistingAccountId) {
            if (code) {
                cleanOAuthParams();
            }
            return;
        }

        let step: any, substep: any;
        if (stateParam) {
            try {
                const state = JSON.parse(decodeURIComponent(stateParam));
                step = state.step;
                substep = state.substep;
            } catch (e) {
                console.error("Invalid state param:", e);
            }
        }

        if (step === 2 && code) {
            const handleChargebeeOAuth = async () => {
                if (substep) {
                    substepFromUrl.current = substep;
                }

                if (substep === 2) {
                    setActiveStep('basic-config');
                    setTimeout(() => {
                        substep2_2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 300);
                }

                const savedFormData = getChargebeeFormData();
                if (savedFormData) {
                    try {
                        const parsedData = JSON.parse(savedFormData);
                        setFormData(prev => ({
                            ...prev,
                            sourceName: parsedData.sourceName || prev.sourceName,
                            secretKey: parsedData.secretKey || prev.secretKey,
                            dataRetentionLocation: parsedData.dataRetentionLocation || prev.dataRetentionLocation,
                            groupIdMapping: parsedData.groupIdMapping || prev.groupIdMapping,
                            environmentId: parsedData.environmentId || prev.environmentId,
                            environmentName: parsedData.environmentName || prev.environmentName,
                            chargebeeEnvironment: parsedData.chargebeeEnvironment || prev.chargebeeEnvironment,
                            syncStartDate: parsedData.syncStartDate ? new Date(parsedData.syncStartDate) : prev.syncStartDate,
                            accountId: ""
                        }));
                    } catch (error) {
                        console.error('Error parsing saved form data:', error);
                    }
                }

                try {
                    console.log('OAuth code received:', code);
                    setAccountIdLoading(true);

                    const result = await GetChargebeeAccountDetails({
                        variables: {
                            input: {
                                accessCode: code
                            }
                        } as any
                    });

                    const accountId = (result.data as any)?.getChargebeeAccountDetails?.accountId;

                    setFormData(prev => ({
                        ...prev,
                        accountId: accountId
                    }));

                    setAccountIdLoading(false);
                    cleanOAuthParams();
                } catch (error) {
                    setAccountIdLoading(false);
                    console.error('Error processing OAuth code:', error);
                    cleanOAuthParams();
                    alert('Failed to connect Chargebee account. Please try again.');
                }
            };

            handleChargebeeOAuth();
        }

        return () => {
            if (window.location.search.includes('code=')) {
                const cleanUrl = `${window.location.pathname}?step=2`;
                window.history.replaceState(null, '', cleanUrl);
            }
        };
    }, [loadedChargebeeData, config]);



    useEffect(() => {
        const hasSourceConfigData = sourceConfigurations?.chargebee && Object.keys(sourceConfigurations.chargebee).length > 0;
        if (didRestoreFromSavedConfigRef.current && hasSourceConfigData && !userChosenStepRef.current) {
            return;
        }

        if (loadedChargebeeData) {
            const urlParams = new URLSearchParams(window.location.search);
            const hasOAuthCode = urlParams.get('code');
            const localData = getChargebeeFormData();
            const hasLoadedAccountId = loadedChargebeeData.formData?.accountId;

            
            const formDataSource = hasSourceConfigData ? sourceConfigurations.chargebee : loadedChargebeeData.formData;

            if (localData && hasOAuthCode && !hasLoadedAccountId) {
                try {

                    const ParsedData = JSON.parse(localData);
                    setFormData(prev => ({
                        ...prev,
                        sourceName: ParsedData.sourceName || formDataSource?.sourceName || "Chargebee",
                        accountId: ParsedData.accountId || formDataSource?.accountId || "",
                        secretKey: ParsedData.secretKey || formDataSource?.secretKey || "",
                        syncStartDate: ParsedData.syncStartDate
                            ? new Date(ParsedData.syncStartDate)
                            : (formDataSource?.syncStartDate ? new Date(formDataSource.syncStartDate) : getDefaultSyncDate()),
                        groupIdMapping: formDataSource?.groupIdMapping || false,
                        environmentId: formDataSource?.environmentId || "",
                        environmentName: formDataSource?.environmentName || "",
                        chargebeeEnvironment: formDataSource?.chargebeeEnvironment || ""
                    }));
                } catch (error) {
                    console.error('Error parsing localStorage:', error);
                }
            } else if (formDataSource) {
                setFormData(prev => ({
                    ...prev,
                    sourceName: formDataSource.sourceName || "Chargebee",
                    subdomain: formDataSource.subdomain || prev.subdomain,
                    apiKey: formDataSource.apiKey || prev.apiKey,
                    accountId: formDataSource.accountId || "",
                    secretKey: formDataSource.secretKey || "",
                    productCatalog: formDataSource.productCatalog || prev.productCatalog,
                    dataRetentionLocation: formDataSource.dataRetentionLocation || "",
                    syncStartDate: formDataSource.syncStartDate
                        ? new Date(formDataSource.syncStartDate)
                        : getDefaultSyncDate(),
                    groupIdMapping: formDataSource.groupIdMapping || false,
                    environmentId: formDataSource.environmentId || "",
                    environmentName: formDataSource.environmentName || "",
                    chargebeeEnvironment: formDataSource.chargebeeEnvironment || ""
                }));

                if (hasOAuthCode) {
                    cleanOAuthParams();
                }
            }

            
            const testingStepsSource = hasSourceConfigData && sourceConfigurations.chargebee.testingSteps 
                ? sourceConfigurations.chargebee.testingSteps 
                : loadedChargebeeData.testingSteps;
            const customerMetaDataKeysSource = hasSourceConfigData && sourceConfigurations.chargebee.customerMetaDataKeys 
                ? sourceConfigurations.chargebee.customerMetaDataKeys 
                : (loadedChargebeeData.testingSteps?.find((step: TestingStep) => step.id === 'schema')?.customerMetaDataKeys);
            const selectedIdentifierKeySource = hasSourceConfigData && sourceConfigurations.chargebee.selectedIdentifierKey 
                ? sourceConfigurations.chargebee.selectedIdentifierKey 
                : (loadedChargebeeData.testingSteps?.find((step: TestingStep) => step.id === 'schema')?.selectedIdentifierKey);
            const groupIdFoundSource = hasSourceConfigData && typeof sourceConfigurations.chargebee.groupIdFound === 'boolean'
                ? sourceConfigurations.chargebee.groupIdFound
                : loadedChargebeeData.groupIdFound;
            const ingestionResultSource = hasSourceConfigData && sourceConfigurations.chargebee.ingestionResult
                ? sourceConfigurations.chargebee.ingestionResult
                : loadedChargebeeData.ingestionResult;

            if (testingStepsSource && testingStepsSource.length > 0) {
                setTestingSteps(testingStepsSource);

                const schemaStep = testingStepsSource.find((step: TestingStep) => step.id === 'schema');

                if (schemaStep) {
                    if (selectedIdentifierKeySource) {
                        setSelectedAccountKey(selectedIdentifierKeySource);
                    }

                    if (customerMetaDataKeysSource && customerMetaDataKeysSource.length > 0) {
                        setCustomerMetaDataKeys(customerMetaDataKeysSource);
                    }
                }
            }

            if (testingStepsSource) {
                const schemaStep = testingStepsSource.find((step: TestingStep) => step.id === 'schema');
                if (schemaStep?.customerMetaDataKeys && schemaStep.customerMetaDataKeys.length > 0) {
                    const groupIdKey = findGroupIdKey(schemaStep.customerMetaDataKeys);
                    if (groupIdKey && !selectedIdentifierKeySource) {
                        setSelectedAccountKey(groupIdKey);
                    }
                }
            }

            if (typeof groupIdFoundSource === 'boolean') {
                setGroupIdFound(groupIdFoundSource);
            }

            if (ingestionResultSource) {
                setIngestionResult(ingestionResultSource);
                
                if (ingestionResultSource.syncStartedAt) {
                    setLastSyncedDate(ingestionResultSource.syncStartedAt);
                }
                
                if (ingestionResultSource.syncStatus) {
                    setSyncStatus(ingestionResultSource.syncStatus as any);
                    
                    if (ingestionResultSource.syncStatus === 'running' || ingestionResultSource.syncStatus === 'pending') {
                        setIsSyncPolling(true);
                    }
                }
            }
            
            const testCompletedSource = hasSourceConfigData && typeof sourceConfigurations.chargebee.testCompleted === 'boolean'
                ? sourceConfigurations.chargebee.testCompleted
                : loadedChargebeeData.testCompleted;
            if (typeof testCompletedSource === 'boolean') {
                setTestCompleted(testCompletedSource);
            }
            
            const isSetupIncompleteSource = hasSourceConfigData && typeof sourceConfigurations.chargebee.isSetupIncomplete === 'boolean'
                ? sourceConfigurations.chargebee.isSetupIncomplete
                : loadedChargebeeData.isSetupIncomplete;
            if (typeof isSetupIncompleteSource === 'boolean') {
                setIsSetupIncomplete(isSetupIncompleteSource);
            }

            const isStep3IncompleteSource = hasSourceConfigData && typeof sourceConfigurations.chargebee.isStep3Incomplete === 'boolean'
                ? sourceConfigurations.chargebee.isStep3Incomplete
                : loadedChargebeeData.isStep3Incomplete;
            if (typeof isStep3IncompleteSource === 'boolean') {
                setIsStep3Incomplete(isStep3IncompleteSource);
            }
            
            const step3CompletedSource = hasSourceConfigData && typeof sourceConfigurations.chargebee.step3Completed === 'boolean'
                ? sourceConfigurations.chargebee.step3Completed
                : loadedChargebeeData.step3Completed;
            if (typeof step3CompletedSource === 'boolean') {
                setStep3Completed(step3CompletedSource);
            }
            
            const step4CompletedSource = hasSourceConfigData && typeof sourceConfigurations.chargebee.step4Completed === 'boolean'
                ? sourceConfigurations.chargebee.step4Completed
                : loadedChargebeeData.step4Completed;
            if (typeof step4CompletedSource === 'boolean') {
                setStep4Completed(prev => {
                    if (step4CompletedSource === true) return true;
                    if (
                        step4CompletedSource === false &&
                        postSaveFlowMode === 'apiKeyOnly' &&
                        resyncStarted &&
                        prev
                    ) {
                        return true;
                    }
                    return step4CompletedSource;
                });
            }
            
            const step4WasEverCompletedSource = hasSourceConfigData && typeof sourceConfigurations.chargebee.step4WasEverCompleted === 'boolean'
                ? sourceConfigurations.chargebee.step4WasEverCompleted
                : loadedChargebeeData.step4WasEverCompleted;
            if (typeof step4WasEverCompletedSource === 'boolean') {
                setStep4WasEverCompleted(step4WasEverCompletedSource);
            }
            if (!substepFromUrl.current) {
                if (userChosenStepRef.current) {
                    setActiveStep(userChosenStepRef.current);
                    userChosenStepRef.current = null;
                } else {
                    const stepData = hasSourceConfigData ? sourceConfigurations.chargebee : loadedChargebeeData;
                    if (stepData.isStep3Incomplete) {
                        setActiveStep('revenue-correlation');
                    } else if (stepData.step4Completed) {
                        setActiveStep('pause-fix');
                    } else if (stepData.step3Completed) {
                        setActiveStep('revenue-correlation');
                    } else if (stepData.isSetupIncomplete) {
                        setActiveStep('basic-config');
                    } else if (stepData.step2Completed) {
                        setActiveStep('revenue-correlation');
                    } else if (stepData.step1Completed) {
                        setActiveStep('basic-config');
                    }
                }
            }

            const notificationEmailsSource = hasSourceConfigData && sourceConfigurations.chargebee.notificationEmails
                ? sourceConfigurations.chargebee.notificationEmails
                : loadedChargebeeData.notificationEmails;
            if (notificationEmailsSource) {
                const additionalEmails = Object.entries(notificationEmailsSource)
                    .filter(([key]) => key !== 'userEmail')
                    .map(([_, value]) => value as string);
                emailManager.setEmails(additionalEmails);
            }

        }
        else if (config) {
            setFormData(prev => ({
                ...prev,
                sourceName: config.sourceName || "Chargebee",
                accountId: config.accountId || "",
                secretKey: config.secretKey || "",
                dataRetentionLocation: config.dataRetentionLocation || "",
                syncStartDate: config.syncStartDate
                    ? new Date(config.syncStartDate)
                    : getDefaultSyncDate(),
                groupIdMapping: config.groupIdMapping || false,
                environmentId: config.environmentId || "",
                environmentName: config.environmentName || "",
                chargebeeEnvironment: config.chargebeeEnvironment || ""
            }));

            if (config?.testingSteps && config.testingSteps.length > 0) {
                setTestingSteps(config.testingSteps);
            }

            if (config.ingestionResult) {
                setIngestionResult(config.ingestionResult);
            }
            if (config.testCompleted) {
                setTestCompleted(config.testCompleted);
            }
            if (typeof config.isSetupIncomplete === 'boolean') {
                setIsSetupIncomplete(config.isSetupIncomplete);
            }

            if (config.isStep3Incomplete) {
                setIsStep3Incomplete(config.isStep3Incomplete);
            }
            if (config.step3Completed) {
                setStep3Completed(config.step3Completed);
            }
            if (config.step4Completed) {
                setStep4Completed(config.step4Completed);
            }
            if (typeof config.step4WasEverCompleted === 'boolean') {
                setStep4WasEverCompleted(config.step4WasEverCompleted);
            }

            if (!substepFromUrl.current) {
                if (userChosenStepRef.current) {
                    setActiveStep(userChosenStepRef.current);
                    userChosenStepRef.current = null;
                } else {
                    if (config.isStep3Incomplete) {
                        setActiveStep('revenue-correlation');
                    } else if (config.step4Completed) {
                        setActiveStep('pause-fix');
                    } else if (config.step3Completed) {
                        setActiveStep('revenue-correlation');
                    } else if (config.isSetupIncomplete) {
                        setActiveStep('basic-config');
                    } else if (config.step2Completed) {
                        setActiveStep('revenue-correlation');
                    } else if (config.step1Completed) {
                        setActiveStep('basic-config');
                    }
                }
            }

            if (config.notificationEmails) {
                const additionalEmails = Object.entries(config.notificationEmails)
                    .filter(([key]) => key !== 'userEmail')
                    .map(([_, value]) => value as string);
                emailManager.setEmails(additionalEmails);
            }
            substepFromUrl.current = null;
        }
    }, [loadedChargebeeData, sourceConfigurations, postSaveFlowMode, resyncStarted]);

    useEffect(() => {

        if (emailManager.state.emails.length > 0 && (loadedChargebeeData || config)) {
            const updatedNotificationEmails = {
                userEmail: getUser()?.email,
                additionalEmails: emailManager.state.emails
            };


            updateSourceConfiguration('chargebee', {
                notificationEmails: updatedNotificationEmails
            });

            setConfig((prev: any) => ({
                ...prev,
                notificationEmails: updatedNotificationEmails
            }));
        }
    }, [emailManager.state.emails]);

    useEffect(() => {
        if (isTesting) {
            const interval = setInterval(() => {
                setDotCount(prev => prev >= 3 ? 1 : prev + 1);
            }, 500);

            return () => clearInterval(interval);
        }
    }, [isTesting]);

    useEffect(() => {
        fetchEnvironments()
    }, [])

    useEffect(() => {
        if (isDemoMode) {
            setFormData((prev) => ({
                ...prev,
                environmentId: DEMO_ENVIRONMENT.id,
                environmentName: DEMO_ENVIRONMENT.displayName,
                
                chargebeeEnvironment: prev.chargebeeEnvironment || 'test'
            }));
        } else {
            const currentEnv = environmentsData?.getEnvironmentsForProduct.environments.find(
                env => env?.displayName === getEnvironmentDisplayName()
            );

            
            
            let defaultChargebeeEnv = '';
            if (currentEnv?.environmentType === EnvironmentType.Production) {
                defaultChargebeeEnv = 'live';
            } else if (currentEnv?.environmentType === EnvironmentType.NonProduction) {
                defaultChargebeeEnv = 'test';
            }

            setFormData((prev) => ({
                ...prev,
                environmentId: `${currentEnv?.id || ''}`,
                environmentName: `${currentEnv?.displayName || ''}`,
                
                chargebeeEnvironment: prev.chargebeeEnvironment || defaultChargebeeEnv
            }));
        }
    }, [environmentsData, isDemoMode])

    const handleInputChange = (field: keyof FormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleNewEnvironment = () => {
        setShowEnvironmentDropdown(false);
        setShowNewEnvironmentForm(true);
    };

    const handleCreateNewEnvironment = async () => {
        if (!newEnvironmentForm.displayName) {
            return;
        }

        try {
            const result = await createEnvironmentCaMutation({
                variables: {
                    input: {
                        name: newEnvironmentForm.displayName.toLowerCase().replace(/\s+/g, '-'),
                        displayName: newEnvironmentForm.displayName,
                        environmentType: newEnvironmentForm.environmentType as EnvironmentType
                    }
                }
            });

            if (result.data?.createEnvironmentCA?.isSuccess) {
                setNewEnvironmentForm({
                    displayName: '',
                    environmentType: EnvironmentType.NonProduction
                });
                setShowNewEnvironmentForm(false);
                await fetchEnvironments();
            } else {
                console.error('Failed to create environment');
            }
        } catch (error) {
            console.error('Error creating environment:', error);
        }
    };

    const handleCancelNewEnvironment = () => {
        setShowNewEnvironmentForm(false);
        setNewEnvironmentForm({
            displayName: '',
            environmentType: EnvironmentType.NonProduction
        });
    };

    
    const saveConfiguration = (actionRequired: boolean) => {
    const prevChargebee = sourceConfigurations?.chargebee ?? {};
    const step2ShouldBeCompleted = prevChargebee.step2Completed === true 
        ? true 
        : !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate);
    
    const config = {
        ...formData,
        step1Completed: !!(formData.environmentId && formData.chargebeeEnvironment),
        step2Completed: step2ShouldBeCompleted,
        step3Completed: true,
        step3CompletedAt: new Date().toISOString(),
        isStep3Incomplete: actionRequired,
        testingSteps: testingSteps.map(step => ({
            ...step,
            ...(step.id === 'schema' ? {
                selectedIdentifierKey: selectedAccountKey,
                customerMetaDataKeys: customerMetaDataKeys
            } : {})
        })),
        testCompleted,
        schemaVerified: true,
        selectedIdentifierKey: selectedAccountKey,
        customerMetaDataKeys: customerMetaDataKeys,
        groupIdFound: groupIdFound,
        isSetupIncomplete: false 
    };
    
    updateSourceConfiguration("chargebee", config);
    setConfig(config);
};

    
    const resetSteps3And4 = (opts?: { keepIngestionResult?: boolean }) => {
        const keepIngestionResult = !!opts?.keepIngestionResult;
        setStep3Completed(false);
        setStep4Completed(false);
        setTestCompleted(false);
        setTestingSteps([
            {
                id: 'connection',
                title: 'Connection',
                loadingText: 'Establishing Connection',
                successText: 'Connected',
                errorText: 'Connection Error',
                status: 'pending'
            },
            {
                id: 'data',
                title: 'Data Retrieval',
                loadingText: 'Retrieving Data Tables',
                successText: 'Data Retrieved',
                errorText: 'Unable to Retrieve Data',
                status: 'pending'
            },
            {
                id: 'schema',
                title: 'Schema Verification',
                loadingText: 'Verifying Schema',
                successText: 'Schema Verified',
                errorText: 'Schema Error Detected',
                status: 'pending'
            }
        ]);
        setCustomerMetaDataKeys([]);
        setSelectedAccountKey('group_id');
        setGroupIdFound(false);
        if (!keepIngestionResult) {
            setIngestionResult(null);
        }
        setIngestionError(null);
        setShowIngestionLoadingSteps(false);
        setSyncStatus('pending');
        setIsSyncPolling(false);
        setLastSyncedDate(null);
        setIsSetupIncomplete(true);
    };

    
    const handleEditSubdomain = () => {
        setOriginalSubdomain(formData.subdomain);
        setIsEditingSubdomain(true);
    };

    
    const handleCancelSubdomain = () => {
        handleInputChange('subdomain', originalSubdomain);
        setIsEditingSubdomain(false);
        setOriginalSubdomain('');
    };

    
    const handleSaveSubdomain = () => {
        setCredentialsErrorDisplayMessage(null);
        let shouldReset = false;
        if (formData.subdomain !== originalSubdomain) {
            resetSteps3And4();
            shouldReset = true;
        }
        setIsEditingSubdomain(false);
        setOriginalSubdomain('');
        
        const config = {
            ...formData,
            step1Completed: !!(formData.environmentId && formData.chargebeeEnvironment),
            step2Completed: !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate),
            step3Completed: shouldReset ? false : step3Completed,
            step4Completed: shouldReset ? false : step4Completed,
            step4WasEverCompleted: step4WasEverCompleted,
            isStep3Incomplete: shouldReset ? false : isStep3Incomplete,
            testingSteps: shouldReset ? [
                {
                    id: 'connection',
                    title: 'Connection',
                    loadingText: 'Establishing Connection',
                    successText: 'Connected',
                    errorText: 'Connection Error',
                    status: 'pending'
                },
                {
                    id: 'data',
                    title: 'Data Retrieval',
                    loadingText: 'Retrieving Data Tables',
                    successText: 'Data Retrieved',
                    errorText: 'Unable to Retrieve Data',
                    status: 'pending'
                },
                {
                    id: 'schema',
                    title: 'Schema Verification',
                    loadingText: 'Verifying Schema',
                    successText: 'Schema Verified',
                    errorText: 'Schema Error Detected',
                    status: 'pending'
                }
            ] : testingSteps,
            testCompleted: shouldReset ? false : testCompleted,
            schemaVerified: sourceConfigurations.chargebee?.schemaVerified || false,
            status: sourceConfigurations.chargebee?.status || 'pending',
            completed: !!(formData.environmentId && formData.chargebeeEnvironment) &&
                       !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate) &&
                       (shouldReset ? false : step3Completed) &&
                       (shouldReset ? false : step4Completed),
            isSetupIncomplete: shouldReset, 
            ingestionResult: shouldReset ? null : sourceConfigurations.chargebee?.ingestionResult || null, 
        };
        updateSourceConfiguration("chargebee", config);
        setConfig(config);
        
        onAutoSave();
    };

    
    const handleEditApiKey = () => {
        setOriginalApiKey(formData.apiKey);
        setIsEditingApiKey(true);
    };

    
    const handleCancelApiKey = () => {
        handleInputChange('apiKey', originalApiKey);
        setIsEditingApiKey(false);
        setOriginalApiKey('');
        // If user cancels, don't treat it as pending change.
        setPendingApiKeyChange(false);
    };

    
    const handleEditSourceName = () => {
        setOriginalSourceName(formData.sourceName);
        setIsEditingSourceName(true);
    };

    
    const handleCancelSourceName = () => {
        handleInputChange('sourceName', originalSourceName);
        setIsEditingSourceName(false);
        setOriginalSourceName('');
    };

    
    const handleSaveSourceName = () => {
        setIsEditingSourceName(false);
        setOriginalSourceName('');
        const config = {
            ...formData,
            step1Completed: !!(formData.environmentId && formData.chargebeeEnvironment),
            step2Completed: !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate),
        };
        updateSourceConfiguration("chargebee", config);
        setConfig(config);
        onAutoSave();
    };

    
    const handleEditProductCatalog = () => {
        setOriginalProductCatalog(formData.productCatalog);
        setIsEditingProductCatalog(true);
    };

    
    const handleCancelProductCatalog = () => {
        handleInputChange('productCatalog', originalProductCatalog as '1.0' | '2.0');
        setIsEditingProductCatalog(false);
        setOriginalProductCatalog('');
        setShowProductCatalogDropdown(false);
    };

    
    const handleSaveProductCatalog = () => {
        setIsEditingProductCatalog(false);
        setOriginalProductCatalog('');
        setShowProductCatalogDropdown(false);
        const config = {
            ...formData,
            step1Completed: !!(formData.environmentId && formData.chargebeeEnvironment),
            step2Completed: !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate),
        };
        updateSourceConfiguration("chargebee", config);
        setConfig(config);
        onAutoSave();
    };

    
    const handleEditSyncDate = () => {
        setOriginalSyncDate(formData.syncStartDate instanceof Date ? new Date(formData.syncStartDate) : null);
        setIsEditingSyncDate(true);
    };

    
    const handleCancelSyncDate = () => {
        if (originalSyncDate) {
            handleInputChange('syncStartDate', originalSyncDate);
        }
        setIsEditingSyncDate(false);
        setOriginalSyncDate(null);
        setShowCalendar(false);
        // If user cancels, don't treat it as pending change.
        setPendingSyncDateChange(false);
    };

    
    const handleSaveSyncDate = async () => {
        setCredentialsErrorDisplayMessage(null);
        let didChange = false;
        if (originalSyncDate && formData.syncStartDate instanceof Date) {
            didChange = formData.syncStartDate.toISOString() !== originalSyncDate.toISOString();
        }
        const shouldReset = !!(didChange && isBasicConfigStepCompleted);
        if (shouldReset) {
            resetSteps3And4({ keepIngestionResult: true });
            setIsConfigEditPendingBigSave(true);
            setPendingSyncDateChange(true);
        }
        setIsEditingSyncDate(false);
        setOriginalSyncDate(null);
        setShowCalendar(false);
        const preservedIngestionResult = sourceConfigurations.chargebee?.ingestionResult || ingestionResult || null;
        const config = {
            ...formData,
            step1Completed: !!(formData.environmentId && formData.chargebeeEnvironment),
            step2Completed: !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate),
            step3Completed: shouldReset ? false : step3Completed,
            step4Completed: shouldReset ? false : step4Completed,
            step4WasEverCompleted: step4WasEverCompleted,
            isStep3Incomplete: shouldReset ? false : isStep3Incomplete,
            testingSteps: shouldReset ? [
                {
                    id: 'connection',
                    title: 'Connection',
                    loadingText: 'Establishing Connection',
                    successText: 'Connected',
                    errorText: 'Connection Error',
                    status: 'pending'
                },
                {
                    id: 'data',
                    title: 'Data Retrieval',
                    loadingText: 'Retrieving Data Tables',
                    successText: 'Data Retrieved',
                    errorText: 'Unable to Retrieve Data',
                    status: 'pending'
                },
                {
                    id: 'schema',
                    title: 'Schema Verification',
                    loadingText: 'Verifying Schema',
                    successText: 'Schema Verified',
                    errorText: 'Schema Error Detected',
                    status: 'pending'
                }
            ] : testingSteps,
            testCompleted: shouldReset ? false : testCompleted,
            schemaVerified: sourceConfigurations.chargebee?.schemaVerified || false,
            status: sourceConfigurations.chargebee?.status || 'pending',
            completed: !!(formData.environmentId && formData.chargebeeEnvironment) &&
                       !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate) &&
                       (shouldReset ? false : step3Completed) &&
                       (shouldReset ? false : step4Completed),
            isSetupIncomplete: shouldReset,
            ingestionResult: preservedIngestionResult,
        };
        updateSourceConfiguration("chargebee", config);
        setConfig(config);
        onAutoSave();
    };

    
    const handleSaveApiKey = async () => {
        setCredentialsErrorDisplayMessage(null);
        let shouldReset = false;
        if (formData.apiKey !== originalApiKey) {
            resetSteps3And4({ keepIngestionResult: true });
            shouldReset = true;
        }
        setIsEditingApiKey(false);
        setOriginalApiKey('');
        if (shouldReset && isBasicConfigStepCompleted) {
            setIsConfigEditPendingBigSave(true);
            setPendingApiKeyChange(true);
        }

        const preservedIngestionResult = sourceConfigurations.chargebee?.ingestionResult || ingestionResult || null;
        const config = {
            ...formData,
            step1Completed: !!(formData.environmentId && formData.chargebeeEnvironment),
            step2Completed: !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate),
            step3Completed: shouldReset ? false : step3Completed,
            step4Completed: shouldReset ? false : step4Completed,
            step4WasEverCompleted: step4WasEverCompleted,
            isStep3Incomplete: shouldReset ? false : isStep3Incomplete,
            testingSteps: shouldReset ? [
                {
                    id: 'connection',
                    title: 'Connection',
                    loadingText: 'Establishing Connection',
                    successText: 'Connected',
                    errorText: 'Connection Error',
                    status: 'pending'
                },
                {
                    id: 'data',
                    title: 'Data Retrieval',
                    loadingText: 'Retrieving Data Tables',
                    successText: 'Data Retrieved',
                    errorText: 'Unable to Retrieve Data',
                    status: 'pending'
                },
                {
                    id: 'schema',
                    title: 'Schema Verification',
                    loadingText: 'Verifying Schema',
                    successText: 'Schema Verified',
                    errorText: 'Schema Error Detected',
                    status: 'pending'
                }
            ] : testingSteps,
            testCompleted: shouldReset ? false : testCompleted,
            schemaVerified: sourceConfigurations.chargebee?.schemaVerified || false,
            status: sourceConfigurations.chargebee?.status || 'pending',
            completed: !!(formData.environmentId && formData.chargebeeEnvironment) &&
                       !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate) &&
                       (shouldReset ? false : step3Completed) &&
                       (shouldReset ? false : step4Completed),
            isSetupIncomplete: shouldReset, 
            ingestionResult: preservedIngestionResult,
        };
        updateSourceConfiguration("chargebee", config);
        setConfig(config);
        
        onAutoSave();
    };

    const saveCurrentStepConfiguration = () => {
        const prevChargebee = sourceConfigurations?.chargebee ?? {};
        // Once step2 is completed (saved in config), it should remain true even if formData is incomplete
        const step2ShouldBeCompleted = prevChargebee.step2Completed === true 
            ? true 
            : !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate);
        
        const config = {
            ...formData,
            step1Completed: !!(formData.environmentId && formData.chargebeeEnvironment),
            step2Completed: step2ShouldBeCompleted,
            step3Completed: step3Completed,
            step4Completed: step4Completed,
            step4WasEverCompleted: step4WasEverCompleted,
            isStep3Incomplete: isStep3Incomplete,
            testingSteps,
            testCompleted,
            schemaVerified: sourceConfigurations.chargebee?.schemaVerified || false,
            status: sourceConfigurations.chargebee?.status || 'pending',
            completed: !!(formData.environmentId && formData.chargebeeEnvironment) &&
                       step2ShouldBeCompleted &&
                       step3Completed &&
                       step4Completed,
            isSetupIncomplete: isSetupIncomplete, 
        };
        updateSourceConfiguration("chargebee", config);
        setConfig(config);
        if (config.step2Completed) {
            removeChargebeeFormData();
        }
    };


    const handleProceedAnyway = () => {
        setSelectedOption("proceed");
        setStep3Completed(true);
        setIsStep3Incomplete(true);
        setActiveStep('pause-fix');
    };


    const handleInviteTeam = (team: TeamId) => {
        setSelectedTeam(team);
        setShowInviteModal(true);
        setIsStep3Incomplete(true);
        setStep3Completed(true);
        
        // Ensure basicConfigSavedThisSession is true so steps don't lock
        setBasicConfigSavedThisSession(true);

        saveConfiguration(true);

        setActiveStep('pause-fix');
    };

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
    };

    const formatDate = (date: Date): string => {
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const completedSteps = stepsConfig.filter(step => step.isCompleted).length;
    const totalSteps = stepsConfig.length;
    const progressPercentage = (completedSteps / totalSteps) * 100;

    const [calendarMonth, setCalendarMonth] = useState(
        formData.syncStartDate instanceof Date ? formData.syncStartDate.getMonth() : new Date().getMonth()
    );
    const [calendarYear, setCalendarYear] = useState(
        formData.syncStartDate instanceof Date ? formData.syncStartDate.getFullYear() : new Date().getFullYear()
    );

    const renderCalendar = () => {
        const currentDate = new Date();

        const firstDay = new Date(calendarYear, calendarMonth, 1);
        const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
        const firstDayOfWeek = firstDay.getDay();
        const daysInMonth = lastDay.getDate();

        const days = [];

        for (let i = 0; i < firstDayOfWeek; i++) {
            days.push(<div key={`empty-${i}`} className="p-2"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(calendarYear, calendarMonth, day);
            const isSelected = formData.syncStartDate instanceof Date ? formData.syncStartDate.toDateString() === date.toDateString() : false;
            const isDisabled = date > currentDate || date < new Date("2020-01-01");

            days.push(
                <button
                    key={day}
                    type="button"
                    onClick={() => {
                        if (!isDisabled) {
                            handleInputChange('syncStartDate', date);
                            setShowCalendar(false);
                        }
                    }}
                    disabled={isDisabled}
                    className={`p-2 text-sm rounded hover:bg-gray-100 ${isSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : ''
                        } ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                    {day}
                </button>
            );
        }

        const handlePreviousMonth = () => {
            if (calendarMonth === 0) {
                setCalendarMonth(11);
                setCalendarYear(calendarYear - 1);
            } else {
                setCalendarMonth(calendarMonth - 1);
            }
        };

        const handleNextMonth = () => {
            if (calendarMonth === 11) {
                setCalendarMonth(0);
                setCalendarYear(calendarYear + 1);
            } else {
                setCalendarMonth(calendarMonth + 1);
            }
        };

        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];

        const years = [];
        for (let year = 2020; year <= currentDate.getFullYear(); year++) {
            years.push(year);
        }

        return (
            <div className="absolute z-10 mb-2 bottom-full bg-white border border-gray-300 rounded-md shadow-lg p-4 w-96">
                <div className="flex items-center justify-between mb-4 gap-2">
                    <button
                        type="button"
                        onClick={handlePreviousMonth}
                        className="p-1 hover:bg-gray-100 rounded"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <div className="flex gap-2">
                        <select
                            value={calendarMonth}
                            onChange={(e) => setCalendarMonth(parseInt(e.target.value))}
                            className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {months.map((month, index) => (
                                <option key={month} value={index}>{month}</option>
                            ))}
                        </select>

                        <select
                            value={calendarYear}
                            onChange={(e) => setCalendarYear(parseInt(e.target.value))}
                            className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <button
                        type="button"
                        onClick={handleNextMonth}
                        className="p-1 hover:bg-gray-100 rounded"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-xs mb-2">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                        <div key={day} className="p-2 text-center font-medium text-gray-500">{day}</div>
                    ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                    {days}
                </div>
            </div>
        );
    };

    const getConnectorLineStyle = () => {
        if (step2ContainerRef.current && step3ContainerRef.current && step4ContainerRef.current) {
            const step2Height = step2ContainerRef.current.getBoundingClientRect().height;
            const step3Height = step3ContainerRef.current.getBoundingClientRect().height;
            const step4Height = step4ContainerRef.current.getBoundingClientRect().height;

            if (activeStep === 'pause-fix') {
                return {
                    top: '4rem',
                    bottom: `${step4Height - 10}px`,
                };
            } else if (activeStep === 'revenue-correlation') {
                return {
                    top: '4rem',
                    bottom: `${step3Height - 10}px`,
                };
            } else if (activeStep === 'basic-config') {
                return {
                    top: '4rem',
                    bottom: `${step2Height - 10}px`,
                };
            }
        }
        return {
            top: '4rem',
            bottom: '4rem',
        };
    };


    const productName = getProductName() || "";

    const getInitials = (name: string) => {
        if (!name || name.trim() === "") return "";

        const words = name.trim().split(/\s+/);
        if (words.length === 1) {
            return words[0].substring(0, 2).toUpperCase();
        }
        return (words[0][0] + words[1][0]).toUpperCase();
    };

    const initials = getInitials(productName);

    const handleAccountIDSelect = () => {
        setChargebeeFormData(JSON.stringify(formData));
        localRef.current = true

        const baseUrl = window.location.origin + window.location.pathname;
        const state = { step: 2, substep: 2 };

        window.location.href =
            `https://connect.chargebee.com/oauth/v2/authorize` +
            `?response_type=code` +
            `&client_id=${process.env.CHARGEBEE_CLIENT_ID}` +
            `&scope=read_write` +
            `&redirect_uri=${encodeURIComponent(baseUrl)}` +
            `&state=${encodeURIComponent(JSON.stringify(state))}`;



    }

    const renderEnvironmentDiagram = (isCompleted: boolean) => {
        if (!formData.environmentId || !formData.chargebeeEnvironment) return null;

        return (
            <div className="flex items-center justify-center space-x-3 py-3">
                {/* Chargebee Environment */}
                <div className="flex flex-col items-center space-y-1 group relative">
                    <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                        Your Chargebee
                    </div>
                    <div className="flex items-center space-x-2 cursor-help">
                        <div className="w-6 h-6 rounded-full border-2 border-blue-600 flex items-center justify-center bg-white">
                            <img src={ChargebeeIcon} alt="Chargebee" className="w-3 h-3" style={{ objectFit: 'contain' }} />
                        </div>
                        <div className="text-xs font-semibold text-gray-700">
                            {formData.chargebeeEnvironment.charAt(0).toUpperCase() + formData.chargebeeEnvironment.slice(1).toLowerCase()}
                        </div>
                    </div>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        Your Chargebee Site: {formData.chargebeeEnvironment.charAt(0).toUpperCase() + formData.chargebeeEnvironment.slice(1).toLowerCase()}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                </div>

                {/* Connection Visual */}
                <div className="flex items-center space-x-1 pt-4">
                    <div className="w-6 h-0.5 bg-blue-600"></div>
                    <div className="w-6 h-6 bg-transparent rounded-full flex items-center justify-center border-2" style={{ borderColor: isCompleted ? '#16a34a' : '#eab308' }}>
                        {isCompleted ? (
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                        ) : (
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        )}
                    </div>
                    <svg className="w-8 h-3" viewBox="0 0 32 12" fill="none">
                        <path d="M0 6 L26 6" stroke="#2563eb" strokeWidth="2" />
                        <path d="M22 2 L30 6 L22 10" stroke="#2563eb" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>

                {/* ThriveStack Environment */}
                <div className="flex flex-col items-center space-y-1 group relative">
                    <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                        {productName || "Product"}
                    </div>
                    <div className="flex items-center space-x-2 cursor-help">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center border-2 border-blue-600 bg-white">
                            <span className="text-blue-600 text-[9px] font-bold">{initials}</span>
                        </div>
                        <div className="text-xs font-semibold text-gray-700 max-w-20 truncate">
                            {formData.environmentName.charAt(0).toUpperCase() + formData.environmentName.slice(1).toLowerCase()}
                        </div>
                    </div>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        Your Chargebee data is syncing to your {formData.environmentName.charAt(0).toUpperCase() + formData.environmentName.slice(1).toLowerCase()} <br /> environment within ThriveStack for "{productName}". Status: {isCompleted ? 'Successful' : 'Pending'}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                </div>
            </div>
        );
    };

    const renderEnvironmentContent = (step: StepConfig) => (
        <div className="space-y-6">
            {/* {step.id === 'environment' && (
                <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Map Environment</h3>
                    <div className="relative group">
                        <Info className="h-4 w-4 text-gray-400 cursor-help" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                            Map your Chargebee environment to your product environment
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                        </div>
                    </div>
                </div>
            )}
            {step.id === 'environment' && (
                <p className="text-sm text-gray-600 mb-6">
                    Map your Chargebee environment with your app environment in ThriveStack for accurate data sync.
                </p>
            )} */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chargebee Site
                    </label>
                    <div className="relative" data-dropdown="chargebee-environment">
                        <button
                            type="button"
                            onClick={() => setShowChargebeeEnvironmentDropdown(!showChargebeeEnvironmentDropdown)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between"
                        >
                            <span className={formData.chargebeeEnvironment ? 'text-gray-900 font-semibold' : 'text-gray-500'}>
                                {formData.chargebeeEnvironment === 'live' ? 'Live' :
                                    formData.chargebeeEnvironment === 'test' ? 'Test' :
                                        'Select Chargebee Site'}
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showChargebeeEnvironmentDropdown ? 'rotate-180' : ''}`} />
                        </button>

                        {showChargebeeEnvironmentDropdown && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                                <div className="py-1">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handleInputChange('chargebeeEnvironment', 'live');
                                            setShowChargebeeEnvironmentDropdown(false);
                                        }}
                                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 hover:text-blue-900 flex items-center justify-between"
                                    >
                                        <span>Live</span>
                                        {formData.chargebeeEnvironment === 'live' && (
                                            <CheckCircle className="w-4 h-4 text-blue-600" />
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handleInputChange('chargebeeEnvironment', 'test');
                                            setShowChargebeeEnvironmentDropdown(false);
                                        }}
                                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 hover:text-blue-900 flex items-center justify-between"
                                    >
                                        <span>Test</span>
                                        {formData.chargebeeEnvironment === 'test' && (
                                            <CheckCircle className="w-4 h-4 text-blue-600" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        ThriveStack Environment
                    </label>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-left flex items-center justify-between cursor-not-allowed">
                        <span className="text-gray-900 font-semibold">
                            {formData.environmentName ?
                                formData.environmentName.charAt(0).toUpperCase() + formData.environmentName.slice(1).toLowerCase() :
                                'Development'
                            }
                        </span>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        To change this, change the environment from the navigation bar
                    </p>
                </div>
            </div>
            {/* Next Button for Step 1 */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                    type="button"
                    onClick={() => {
                        saveCurrentStepConfiguration();
                        setActiveStep('basic-config');
                        setTimeout(() => {
                            step2ContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                        onAutoSave()
                    }}
                    disabled={!(formData.environmentId && formData.chargebeeEnvironment)}
                    className={`px-6 py-2 rounded-md font-medium transition-colors ${formData.environmentId && formData.chargebeeEnvironment
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                >
                    Next
                </button>
            </div>
        </div>
    );

    const renderBasicConfigContent = () => {
        const isStep2Complete = !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate);
        const isSubdomainLocked = isBasicConfigStepCompleted && !!formData.subdomain;
        const isProductCatalogLocked = isBasicConfigStepCompleted && testCompleted;

        return (
            <div className="space-y-8">
                {/* Source Name Section */}
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">2.1 Source Name</h3>
                        {formData.sourceName && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                    </div>
                    <div className="ml-7">
                        <p className="text-sm text-gray-600 mb-4">Give this data source a memorable name to easily identify it in your dashboard.</p>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Enter source name"
                                value={formData.sourceName}
                                onChange={(e) => handleInputChange('sourceName', e.target.value)}
                                readOnly={isBasicConfigStepCompleted && !!sourceConfigurations.chargebee?.sourceName && !isEditingSourceName}
                                className={`w-96 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                    isBasicConfigStepCompleted && !isEditingSourceName && !!sourceConfigurations.chargebee?.sourceName 
                                        ? 'bg-gray-50 cursor-not-allowed' 
                                        : ''
                                }`}
                            />
                            {isBasicConfigStepCompleted && sourceConfigurations.chargebee?.sourceName && !isEditingSourceName && (
                                <button
                                    type="button"
                                    onClick={handleEditSourceName}
                                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                                    title="Edit"
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                            )}
                            {isEditingSourceName && (
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleSaveSourceName}
                                        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors whitespace-nowrap"
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancelSourceName}
                                        className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                        {errors.sourceName && <p className="text-red-600 text-sm mt-1">{errors.sourceName}</p>}
                    </div>
                </div>

                {/* Chargebee Subdomain Section */}
                <div ref={substep2_2Ref}>
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">2.2 Site Name (Subdomain)</h3>
                        {formData.subdomain && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                        <div className="relative group">
                            <Info className="h-4 w-4 text-gray-400 cursor-help" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                Your site name appears in your Chargebee URL: {'{sitename}'}.chargebee.com
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                        </div>
                    </div>
                    <div className="ml-7">
                        <p className="text-sm text-gray-600 mb-4">
                            Enter your Chargebee site name (subdomain). For example, if your Chargebee URL is <span className="font-mono bg-gray-100 px-1 rounded">mycompany.chargebee.com</span>, enter <span className="font-mono bg-gray-100 px-1 rounded">mycompany</span>.
                        </p>
                        <div className="relative flex items-center gap-2">
                            <div className="flex items-center max-w-md">
                                {isSubdomainLocked ? (
                                    <div
                                        className="w-64 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50 cursor-not-allowed select-none text-gray-900 font-semibold"
                                        onCopy={(e) => e.preventDefault()}
                                        onCut={(e) => e.preventDefault()}
                                        onDragStart={(e) => e.preventDefault()}
                                        onMouseDown={(e) => e.preventDefault()}
                                    >
                                        {formData.subdomain || '—'}
                                    </div>
                                ) : (
                                    <input
                                        type="text"
                                        placeholder="mycompany"
                                        value={formData.subdomain}
                                        onChange={(e) => handleInputChange('subdomain', e.target.value)}
                                        className="w-64 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                )}
                                <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-gray-600 select-none">
                                    .chargebee.com
                                </span>
                            </div>
                            {isSubdomainLocked && (
                                <div className="flex items-center gap-2">
                                    <div className="relative group">
                                        <Lock className="w-4 h-4 text-gray-500" />
                                    </div>
                                    <div className="relative group">
                                        <Info className="w-4 h-4 text-gray-500 cursor-help" />
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 w-80">
                                            To use a different Chargebee subdomain, <a href="#" onClick={(e) => { e.preventDefault(); onOpenIntegrationsList(); }} className="font-semibold underline">go to Integrations list</a>, delete this connection, and create a new one.
                                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!isSubdomainLocked && credentialsErrorDisplayMessage && (
                                <span className="flex items-center gap-1.5 text-sm text-red-600">
                                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                    Invalid credentials
                                </span>
                            )}
                            {isEditingSubdomain && (
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleSaveSubdomain}
                                        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors whitespace-nowrap"
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancelSubdomain}
                                        className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                        {isEditingSubdomain && (
                            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-sm text-yellow-800">
                                    <strong>Warning:</strong> Changing the site name will reset the setup and you need to run connection test and ingestion again with the updated credentials.
                                </p>
                            </div>
                        )}
                        {errors.subdomain && <p className="text-red-600 text-sm mt-1">{errors.subdomain}</p>}
                    </div>
                </div>

                {/* API Key Section */}
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">2.3 API Key</h3>
                        {formData.apiKey && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                        <div className="relative group">
                            <Info className="h-4 w-4 text-gray-400 cursor-help" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                Get your secret key from Chargebee dashboard and paste it here for API access
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                        </div>
                    </div>
                    <div className="ml-7">
                        <div className="flex items-start mb-4">
                            <p className="text-sm text-gray-600">
                                Your Chargebee API key helps ThriveStack to pull data from your Chargebee account.
                            </p>
                            <button
                                onClick={() => {
                                    setIsSetupGuideOpen(true);
                                    setTimeout(() => {
                                        setupGuideStep3Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 100);
                                }}
                                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium whitespace-nowrap ml-4"
                            >
                                Show Documentation
                            </button>
                        </div>

                        <div className="mb-4">
                            <p className="text-sm text-gray-700 mb-2">
                                1. Get your API key from Chargebee{' '}
                                <a
                                    href={formData.subdomain
                                        ? `https://${formData.subdomain}.chargebee.com/apikeys_and_webhooks/api`
                                        : 'https://www.chargebee.com/docs/2.0/api_keys.html'
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
                                >
                                    Get your API key from Chargebee
                                    <ExternalLink className="h-3 w-3" />
                                </a>
                            </p>
                            <p className="text-sm text-gray-700">
                                2. Paste it below
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            <div className="relative max-w-md">
                                <input
                                    type={showSecretKey ? "text" : "password"}
                                    placeholder="live_••••••••••••••••"
                                    value={formData.apiKey}
                                    onChange={(e) => handleInputChange('apiKey', e.target.value)}
                                    readOnly={isBasicConfigStepCompleted && !!sourceConfigurations.chargebee?.apiKey && !isEditingApiKey}
                                    className={`w-96 px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                        isBasicConfigStepCompleted && !isEditingApiKey && !!sourceConfigurations.chargebee?.apiKey 
                                            ? 'bg-gray-50 cursor-not-allowed' 
                                            : ''
                                    }`}
                                />
                                {(!isEditingApiKey || !sourceConfigurations.chargebee?.apiKey) && (
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        onClick={() => setShowSecretKey(!showSecretKey)}
                                    >
                                        {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                )}
                            </div>
                            {isBasicConfigStepCompleted && sourceConfigurations.chargebee?.apiKey && !isEditingApiKey && (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleEditApiKey}
                                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                                        title="Edit"
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    {credentialsErrorDisplayMessage && (
                                        <span className="flex items-center gap-1.5 text-sm text-red-600">
                                            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                            Invalid credentials
                                        </span>
                                    )}
                                </>
                            )}
                            {isEditingApiKey && (
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleSaveApiKey}
                                        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors whitespace-nowrap"
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancelApiKey}
                                        className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                        {isEditingApiKey && (
                            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-sm text-yellow-800">
                                    <strong>Warning:</strong> Changing the API key will reset the setup and you need to run connection test and ingestion again with the updated credentials.
                                </p>
                            </div>
                        )}
                        {errors.apiKey && <p className="text-red-600 text-sm mt-1">{errors.apiKey}</p>}
                    </div>
                </div>

                {/* Product Catalog Section */}
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">2.4 Product Catalog Version</h3>
                        {formData.productCatalog && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                        <div className="relative group">
                            <Info className="h-4 w-4 text-gray-400 cursor-help" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                Select your Chargebee Product Catalog version
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                        </div>
                    </div>
                    <div className="ml-7">
                        <p className="text-sm text-gray-600 mb-4">
                            Select the Product Catalog version used in your Chargebee account.{' '}
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setIsSetupGuideOpen(true);
                                    setTimeout(() => setupGuideStep3Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                                }}
                                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium whitespace-nowrap ml-4"
                            >
                                Show documentation
                            </a>
                        </p>
                        <div className="flex items-center gap-2">
                            <div className="relative max-w-md" data-dropdown="product-catalog">
                                {isProductCatalogLocked ? (
                                    <div
                                        className="w-96 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed select-none flex items-center justify-between"
                                        onCopy={(e) => e.preventDefault()}
                                        onCut={(e) => e.preventDefault()}
                                        onDragStart={(e) => e.preventDefault()}
                                        onMouseDown={(e) => e.preventDefault()}
                                    >
                                        <span className="text-gray-900 font-semibold">
                                            {formData.productCatalog === '2.0' ? 'Product Catalog 2.0' :
                                                formData.productCatalog === '1.0' ? 'Product Catalog 1.0' :
                                                    '—'}
                                        </span>
                                        <Lock className="w-4 h-4 text-gray-500" />
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setShowProductCatalogDropdown(!showProductCatalogDropdown)}
                                        className="w-96 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between bg-white"
                                    >
                                        <span className={formData.productCatalog ? 'text-gray-900 font-semibold' : 'text-gray-500'}>
                                            {formData.productCatalog === '2.0' ? 'Product Catalog 2.0' :
                                                formData.productCatalog === '1.0' ? 'Product Catalog 1.0' :
                                                    'Select Product Catalog'}
                                        </span>
                                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showProductCatalogDropdown ? 'rotate-180' : ''}`} />
                                    </button>
                                )}

                                {showProductCatalogDropdown && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                                        <div className="py-1">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    handleInputChange('productCatalog', '2.0');
                                                    setShowProductCatalogDropdown(false);
                                                }}
                                                className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 hover:text-blue-900 flex items-center justify-between"
                                            >
                                                <span>Product Catalog 2.0</span>
                                                {formData.productCatalog === '2.0' && (
                                                    <CheckCircle className="w-4 h-4 text-blue-600" />
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (!isEditingProductCatalog) {
                                                        handleInputChange('productCatalog', '1.0');
                                                        setShowProductCatalogDropdown(false);
                                                    }
                                                }}
                                                className="w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 transition-colors flex items-center justify-between border-t border-gray-200"
                                            >
                                                <span>Product Catalog 1.0</span>
                                                {formData.productCatalog === '1.0' && (
                                                    <CheckCircle className="w-4 h-4 text-blue-600" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {isProductCatalogLocked && (
                                <div className="relative group">
                                    <Info className="w-4 h-4 text-gray-500 cursor-help" />
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 w-80">
                                        To use a different Product Catalog version, <a href="#" onClick={(e) => { e.preventDefault(); onOpenIntegrationsList(); }} className="font-semibold underline">go to Integrations list</a>, delete this connection, and create a new one.
                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                </div>
                            )}
                            {isEditingProductCatalog && (
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleSaveProductCatalog}
                                        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors whitespace-nowrap"
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancelProductCatalog}
                                        className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {/* Sync Start Date Section */}
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">2.5 Sync Start Date</h3>
                        {formData.syncStartDate && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                    </div>
                    <div className="ml-7">
                        <p className="text-sm text-gray-600 mb-4">Choose the date from which you want to start syncing historical payment data.</p>
                        <div className="flex items-center gap-2">
                            <div className="relative max-w-md">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!isBasicConfigStepCompleted || isEditingSyncDate || !sourceConfigurations.chargebee?.syncStartDate) {
                                            setShowCalendar(!showCalendar);
                                        }
                                    }}
                                    disabled={isBasicConfigStepCompleted && !!sourceConfigurations.chargebee?.syncStartDate && !isEditingSyncDate}
                                    className={`w-96 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between ${
                                        isBasicConfigStepCompleted && !isEditingSyncDate && !!sourceConfigurations.chargebee?.syncStartDate 
                                            ? 'bg-gray-50 cursor-not-allowed' 
                                            : ''
                                    }`}
                                >
                                    <span className={formData.syncStartDate ? "text-gray-900" : "text-gray-500"}>
                                        {formData.syncStartDate instanceof Date ? formatDate(formData.syncStartDate) : "Pick a date"}
                                    </span>
                                    <CalendarIcon className="h-4 w-4 text-gray-400" />
                                </button>
                                {showCalendar && renderCalendar()}
                            </div>
                            {isBasicConfigStepCompleted && sourceConfigurations.chargebee?.syncStartDate && !isEditingSyncDate && (
                                <button
                                    type="button"
                                    onClick={handleEditSyncDate}
                                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                                    title="Edit"
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                            )}
                            {isEditingSyncDate && (
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleSaveSyncDate}
                                        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors whitespace-nowrap"
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancelSyncDate}
                                        className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                        {isEditingSyncDate && (
                            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-sm text-yellow-800">
                                    <strong>Warning:</strong> Changing the sync start date will reset the setup and you need to run connection test and ingestion again with the updated settings.
                                </p>
                            </div>
                        )}
                        {errors.syncStartDate && <p className="text-red-600 text-sm mt-1">{errors.syncStartDate.toString()}</p>}
                    </div>
                </div>

                {/* Next Button for Step 2 */}
                <div className="flex justify-end pt-4 border-t border-gray-200">
                    {(() => {
                        const isBigSaveDisabled =
                            !isStep2Complete ||
                            isEditingSourceName ||
                            isEditingSubdomain ||
                            isEditingApiKey ||
                            isEditingProductCatalog ||
                            isEditingSyncDate;

                        return (
                    <button
                        type="button"
                        onClick={() => {
                            setBasicConfigSavedThisSession(true);
                            setIsSetupIncomplete(false);
                            
                            // Set manual step change BEFORE any config updates to prevent auto-navigation
                            setIsManualStepChange(true);
                            userChosenStepRef.current = 'revenue-correlation';

                            if (isConfigEditPendingBigSave) {
                                // Calculate and set the mode for determining resync behavior
                                const mode: PostSaveFlowMode =
                                    pendingApiKeyChange && pendingSyncDateChange ? 'both' :
                                    pendingSyncDateChange ? 'syncDateOnly' :
                                    pendingApiKeyChange ? 'apiKeyOnly' :
                                    'none';

                                setPostSaveFlowMode(mode);
                                setIsConfigEditPendingBigSave(false);
                                
                                // Persist which fields were changed 
                                const updatedConfig = {
                                    ...formData,
                                    pendingApiKeyChange: pendingApiKeyChange,
                                    pendingSyncDateChange: pendingSyncDateChange,
                                    step1Completed: !!(formData.environmentId && formData.chargebeeEnvironment),
                                    step2Completed: !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.productCatalog && formData.syncStartDate)
                                };
                                updateSourceConfiguration('chargebee', updatedConfig);
                                setConfig(updatedConfig);
                            } else {
                                saveCurrentStepConfiguration();
                            }
                            
                            removeChargebeeFormData();
                            onAutoSave();

                            // Navigate to Step 3
                            setActiveStep('revenue-correlation');
                            setTimeout(() => {
                                step3ContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 100);
                        }}
                        disabled={isBigSaveDisabled}
                        className={`px-6 py-2 rounded-md font-medium transition-colors ${
                            isBigSaveDisabled
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                    >
                        Save
                    </button>
                        );
                    })()}
                </div>
                <DemoChargebeeModal
                    isOpen={showDemoModal}
                    onClose={() => setShowDemoModal(false)}
                    onConnect={handleDemoChargebeeConnect}
                />
            </div>
        );
    };

    const handleTestSync = async (scenario: keyof typeof DEMO_TEST_SCENARIOS = 'SUCCESS') => {
        setIsTesting(true);
        setTestCompleted(false);
        setSelectedOption(null);
        setIsTablesExpanded(false);
        setIsStep3Incomplete(false);
        setValidationErrors(null);
        setCredentialsErrorDisplayMessage(null);
        setTestingSteps(prev => prev.map(step => ({ ...step, status: 'pending' as const, missingEntities: [] })));

        try {
            
            setTestingSteps(prev => prev.map((step, index) =>
                index === 0 ? { ...step, status: 'loading' as const } : step
            ));
            await new Promise(resolve => setTimeout(resolve, 500));

            const apiResult: any = await testIntegration(scenario);
            if (!apiResult) {
                throw new Error('CHARGEBEE_TEST_CONNECTION_NO_RESPONSE');
            }

            const errors: typeof validationErrors = {};
            if (apiResult?.validSiteTypeMessage) {
                errors.siteType = apiResult.validSiteTypeMessage;
            }
            if (apiResult?.validSiteNameMessage) {
                errors.siteName = apiResult.validSiteNameMessage;
            }
            if (apiResult?.validProductCatalogMessage) {
                errors.productCatalog = apiResult.validProductCatalogMessage;
            }
            if (apiResult?.credentialsMessage) {
                errors.credentials = apiResult.credentialsMessage;
                const msg = (apiResult.credentialsMessage as string).toLowerCase();
                if (msg.includes('site_not_found')) {
                    setCredentialsErrorDisplayMessage('Invalid Site Name and or API key');
                } else {
                    setCredentialsErrorDisplayMessage('Invalid credentials');
                }
                setTimeout(() => {
                    userChosenStepRef.current = 'basic-config';
                    setActiveStep('basic-config');
                    setTimeout(() => {
                        step2ContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                }, 1500);
            }

            if (apiResult?.entitySyncResults) {
                const failedEntities = apiResult.entitySyncResults
                    .filter((result: any) => !result.success)
                    .map((result: any) => ({
                        entityName: result.entityName,
                        errorMessage: result.errorMessage
                    }));
                if (failedEntities.length > 0) {
                    errors.entitySync = failedEntities;
                }
            }

            if (Object.keys(errors).length > 0) {
                setValidationErrors(errors);
                setTestingSteps(prev => prev.map((step, index) =>
                    index === 0 ? { ...step, status: 'error' as const } : step
                ));
                setIsTesting(false);
                setTestCompleted(true);
                return;
            }

            setTestingSteps(prev => prev.map((step, index) =>
                index === 0 ? { ...step, status: 'success' as const } : step
            ));
            await new Promise(resolve => setTimeout(resolve, 800));

            setTestingSteps(prev => prev.map((step, index) =>
                index === 1 ? { ...step, status: 'loading' as const } : step
            ));
            setTimeout(() => setIsTablesExpanded(true), 300);
            await new Promise(resolve => setTimeout(resolve, 1000));

            const failedEntityNames = apiResult?.entitySyncResults
                ?.filter((result: any) => !result.success)
                .map((result: any) => result.entityName) || [];
            const hasFailedEntities = failedEntityNames.length > 0;

            setTestingSteps(prev => prev.map((step, index) =>
                index === 1 ? {
                    ...step,
                    status: hasFailedEntities ? 'error' as const : 'success' as const,
                    missingEntities: failedEntityNames
                } : step
            ));

            
            if (hasFailedEntities) {
                setTimeout(() => setIsTablesExpanded(false), 1200);
                setIsTesting(false);
                setTestCompleted(true);
                setStep3Completed(true);
                setIsStep3Incomplete(true);
                return;
            }

            setTimeout(() => setIsTablesExpanded(false), 1200);
            await new Promise(resolve => setTimeout(resolve, 800));

            setTestingSteps(prev => prev.map((step, index) =>
                index === 2 ? { ...step, status: 'loading' as const } : step
            ));
            await new Promise(resolve => setTimeout(resolve, 500));

            const hasMetadataKeys = apiResult?.customerMetaDataKeys && apiResult.customerMetaDataKeys.length > 0;

            let schemaStatus: 'success' | 'error';

            if (!hasMetadataKeys) {
                schemaStatus = 'error';
            } else {
                schemaStatus = 'success';
            }

            setTestingSteps(prev => prev.map((step, index) =>
                index === 2 ? {
                    ...step,
                    status: schemaStatus,
                    selectedIdentifierKey: selectedAccountKey
                } : step
            ));

            await new Promise(resolve => setTimeout(resolve, 800));

            setStep3Completed(true);
            
            // In resync flow, preserve isStep3Incomplete; in first setup, set based on groupIdFound
            if (!step4WasEverCompleted) {
                setIsStep3Incomplete(!groupIdFound);
            }
            // If step4WasEverCompleted (resync flow), keep existing isStep3Incomplete value 

            

        } catch (error) {
            console.error('Test failed:', error);
            
            setTestingSteps(prev => prev.map((step, idx) => {
                if (idx === 0) return { ...step, status: 'error' as const, missingEntities: [] };
                return { ...step, status: 'pending' as const, missingEntities: [] };
            }));
            setTestCompleted(true);
        }

        setIsTesting(false);
        setTestCompleted(true);
    };

    const renderTestingSteps = () => {
        return (
            <div className="mt-4 space-y-3 border border-gray-200 p-4 rounded-lg">
                {testingSteps.map((step, index) => (
                    <div key={step.id} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1 text-lg">
                            {step.status === 'loading' && (
                                <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                            )}
                            {step.status === 'success' && (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                            )}
                            {step.status === 'error' && (
                                <AlertTriangle className="w-4 h-4 text-red-600" />
                            )}
                            {step.status === 'pending' && (
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300"></div>
                            )}
                        </div>

                        <div className="flex-1">
                            <div className="font-medium text-gray-900 text-lg">
                                {step.status === 'loading' && step.loadingText}
                                {step.status === 'success' && step.successText}
                                {step.status === 'error' && (
                                    step.id === 'data' ? 'Failed to Retrieve Data' : (
                                        step.id === 'connection' && credentialsErrorDisplayMessage ? (
                                            <span className="flex items-center gap-2 flex-wrap">
                                                {step.errorText}
                                                <span className="text-red-600 font-normal text-base"> - {credentialsErrorDisplayMessage}</span>
                                            </span>
                                        ) : step.errorText
                                    )
                                )}
                                {step.status === 'pending' && step.loadingText}
                            </div>

                            {/* Show tables during Test */}
                            {step.id === 'data' && step.status !== 'pending' && (
                                <div className="mt-2">
                                    <button
                                        type="button"
                                        onClick={() => setIsTablesExpanded(!isTablesExpanded)}
                                        className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm font-medium mb-2"
                                    >
                                        <ChevronDown className={`w-4 h-4 transition-transform ${isTablesExpanded ? '' : '-rotate-90'}`} />
                                        <span>List of Entities Syncing</span>
                                    </button>

                                    {isTablesExpanded && (
                                        <div className="border-l-2 border-gray-200 pl-4 ml-6 transition-all duration-500 ease-in-out transform">
                                            <div className="space-y-2">
                                                <div className="text-sm text-gray-600">
                                                    Total tables in Chargebee: <span className="font-medium text-gray-900">{CHARGEBEE_TABLES.available.length}</span>
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    Tables to be synced to TS: <span className="font-medium text-gray-900">{CHARGEBEE_TABLES.willSync.length}</span>
                                                    <ul className="flex flex-col ml-1 mt-2 space-y-1">
                                                        {CHARGEBEE_TABLES.willSync.map(entity => {
                                                            const isMissing = step.missingEntities?.includes(entity);
                                                            return (
                                                                <li key={entity} className="flex items-center space-x-2 text-sm">
                                                                    {isMissing ? (
                                                                        <X className="w-4 h-4 text-red-600 flex-shrink-0" />
                                                                    ) : (
                                                                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                                                                    )}
                                                                    <span className={isMissing ? 'text-red-700 font-medium' : 'text-gray-700'}>
                                                                        {entity}
                                                                    </span>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Missing entities error for data retrieval */}
                            {step.id === 'data' && step.status === 'error' && step.missingEntities && step.missingEntities.length > 0 && (
                                <div className="space-y-4 mt-3">
                                    <div className="p-4 bg-red-50 rounded-md">
                                        <div className="space-y-3">
                                            <h4 className="font-semibold text-gray-900 text-base">
                                                Unable to retrieve some entities — <span className="font-normal">Your secret key doesn't have access to {step.missingEntities?.length || 0} / {CHARGEBEE_TABLES.willSync.length} required Chargebee {step.missingEntities?.length === 1 ? 'table' : 'tables'}.</span>
                                            </h4>
                                            <h4 className="font-semibold text-gray-900 text-base">
                                                To resolve this, you need to{' '}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setIsSetupGuideOpen(true);
                                                        setTimeout(() => {
                                                            const setupSection = document.querySelector('[data-section="setup-chargebee"]');
                                                            if (setupSection) {
                                                                setupSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                            }
                                                        }, 100);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800 underline font-medium"
                                                >
                                                    configure your secret key's permissions
                                                </button>
                                                .
                                            </h4>

                                            <div className="bg-white p-3 rounded">
                                                <div className="text-sm font-medium text-gray-900 mb-2">All tables to be synced:</div>
                                                <ul className="space-y-1">
                                                    {CHARGEBEE_TABLES.willSync.map(entity => {
                                                        const isMissing = step.missingEntities?.includes(entity);
                                                        return (
                                                            <li key={entity} className="flex items-center space-x-2 text-sm">
                                                                {isMissing ? (
                                                                    <X className="w-4 h-4 text-red-600 flex-shrink-0" />
                                                                ) : (
                                                                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                                                )}
                                                                <span className={isMissing ? 'text-red-700 font-medium' : 'text-gray-700'}>
                                                                    {entity}
                                                                </span>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    type="button"
                                                    className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-md font-medium text-sm transition-colors"
                                                    onClick={() => handleTestSync('SUCCESS')}
                                                >
                                                    Retry Test
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Schema Error Info - ONLY show when NO metadata keys */}
                            {step.status === 'error' &&
                                step.id === 'schema' &&
                                (!customerMetaDataKeys || customerMetaDataKeys.length === 0) && (
                                    <div className="space-y-3 mt-3">
                                        <p className="text-sm text-gray-700">
                                            Account identifier key is missing in Chargebee customer metadata. Fix it to enable correlation across product, marketing and revenue data.
                                        </p>
                                        <FixInstructionsContent source="chargebee" accountIdentifierKey={selectedAccountKey} />
                                        <button
                                            type="button"
                                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm transition-colors"
                                            onClick={() => {
                                                handleInviteTeam(TeamId.REVOPS_TEAMS);
                                                saveConfiguration(true);
                                                setActiveStep('pause-fix');
                                                setStep3Completed(true);
                                            }}
                                        >
                                            Invite and Proceed
                                        </button>
                                    </div>
                                )}

                            {/* Account Identifier Dropdown - Show ONLY when schema step has succeeded and metadata keys exist */}
                            {step.id === 'schema' &&
                                step.status === 'success' &&
                                customerMetaDataKeys &&
                                customerMetaDataKeys.length > 0 && (
                                    <>
                                    <div className="space-y-4 mt-3">
                                        <div className="p-4 bg-gray-50 rounded-lg">
                                            <div className="space-y-3">
                                                <div>
                                                    <p className="text-sm text-gray-900 mb-3">
                                                        To uniquely identify the account information and to correlate it with revenue data, we look at the following key. This key enables seamless correlation across revenue, marketing, and product data.
                                                    </p>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Account Identifier Key
                                                    </label>
                                                    <div className="relative">
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowAccountKeyDropdown(!showAccountKeyDropdown)}
                                                            className="w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between"
                                                        >
                                                            <span className="text-gray-900 font-medium">{selectedAccountKey}</span>
                                                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showAccountKeyDropdown ? 'rotate-180' : ''}`} />
                                                        </button>

                                                        {showAccountKeyDropdown && (
                                                            <div className="absolute z-10 w-64 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                                                <div className="py-1">
                                                                    {customerMetaDataKeys.map((key) => (
                                                                        <button
                                                                            key={key}
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setSelectedAccountKey(key);
                                                                                setShowAccountKeyDropdown(false);
                                                                                setTestingSteps(prev => prev.map(step =>
                                                                                    step.id === 'schema'
                                                                                        ? {
                                                                                            ...step,
                                                                                            selectedIdentifierKey: key,
                                                                                            customerMetaDataKeys: customerMetaDataKeys
                                                                                        }
                                                                                        : step
                                                                                ));
                                                                            }}
                                                                            className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 hover:text-blue-900 flex items-center justify-between"
                                                                        >
                                                                            <span>{key}</span>
                                                                            {selectedAccountKey === key && (
                                                                                <Check className="w-4 h-4 text-blue-600" />
                                                                            )}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                        </div>
                                                    )}

                                                    {groupIdFound && (
                                                        <>
                                                            <p className="flex items-center gap-x-1 text-sm bg-amber-50 text-amber-700 rounded-xl px-5 py-4 mb-3 mt-2">
                                                                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                                                <span>
                                                                    Group ID is set as the default account identifier. Please select an alternative key from the dropdown menu if this is incorrect.
                                                                </span>
                                                            </p>
                                                        </>
                                                    )}

                                                    {!groupIdFound && (
                                                        <div className="mt-3">
                                                            <div className="bg-amber-50 text-amber-700 rounded-xl px-5 py-4">
                                                                <div className="flex items-start gap-2">
                                                                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                                                    <div className="text-sm">
                                                                        <p className="mb-1">group_id was not found in the customer metadata from Chargebee.</p>
                                                                        <ul className="pl-5 space-y-2">
                                                                            <li className="list-disc">
                                                                                <span className="font-medium">Option 1:</span> Select the correct account identifier from the dropdown above.
                                                                            </li>
                                                                            <li className="list-none text-center text-xs font-semibold uppercase tracking-wide text-amber-700">
                                                                                OR
                                                                            </li>
                                                                            <li className="list-disc">
                                                                                <span className="font-medium">Option 2:</span> Resolve this by using the AI-assisted or manual steps below to add the missing key in customer metadata of Chargebee.
                                                                            </li>
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <p className="flex items-center text-sm text-gray-700 mx-2 mb-2">
                                                        If your product telemetry's account identifier key is missing from the dropdown, use the tabs below to resolve this.
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowFixInstructionsModal(true)}
                                                            className="text-blue-600 underline hover:text-blue-800 ml-1"
                                                        >
                                                            See the guide
                                                        </button>
                                                        .
                                                    </p>
                                                    <FixInstructionsContent source="chargebee" accountIdentifierKey={selectedAccountKey} defaultCollapsed={groupIdFound} />
                                                    </div>
                                                </div>

                                                {/* BOTH BUTTONS ALWAYS SHOWN */}
                                                <div className="flex items-center gap-3 pt-2">
                                                    <button
                                                        type="button"
                                                        onClick={handleInviteAndProceedWithMetadata}
                                                        disabled={isUpdatingSource}
                                                        className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
                                                            isUpdatingSource
                                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                                                        }`}
                                                    >
                                                        {isUpdatingSource ? (
                                                            <span className="inline-flex items-center gap-2">
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                                Saving...
                                                            </span>
                                                        ) : updateSourceError ? (
                                                            'Retry Invite and Proceed'
                                                        ) : (
                                                            'Invite and Proceed'
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleSaveAndProceed}
                                                        disabled={isUpdatingSource}
                                                        className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
                                                            isUpdatingSource
                                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-300'
                                                                : 'bg-white border border-blue-600 hover:bg-blue-50 text-blue-600'
                                                        }`}
                                                    >
                                                        {updateSourceError ? 'Retry Save and Proceed' : 'Save and Proceed'}
                                                    </button>
                                                    {updateSourceError && (
                                                        <p className="text-red-600 text-sm">{updateSourceError}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    </>
                                )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderRevenueCorrelationContent = () => {
        return (
            <div className="space-y-6">
                <div>
                    <p className="text-gray-700 leading-relaxed text-sm">
                        We will use the credentials from <span className="font-medium text-gray-900">Basic Configuration</span> (step 2)
                        to test the connection with Chargebee and sync sample data of 200 Transactions.
                    </p>

                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <button
                            type="button"
                            className={`px-4 py-2 rounded-md font-medium transition-all duration-200 flex items-center space-x-2 min-w-[120px] justify-center ${isTesting
                                ? 'bg-blue-600 text-white cursor-default'
                                : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-sm hover:shadow-md'
                                }`}
                            onClick={() => handleTestSync('SUCCESS')}
                            disabled={isTesting}
                        >
                            {isTesting ? (
                                <>
                                    <Database className="w-4 h-4" />
                                    <span>Testing{'.'.repeat(dotCount)}</span>
                                </>
                            ) : testCompleted ? (
                                <>
                                    <Database className="w-4 h-4" />
                                    <span>Retry</span>
                                </>
                            ) : (
                                <>
                                    <Database className="w-4 h-4" />
                                    <span>Test Data Sync</span>
                                </>
                            )}
                        </button>

                        {isDemoMode && !isTesting && (
                            <>
                                <button
                                    type="button"
                                    className="px-4 py-2 rounded-md font-medium transition-all duration-200 flex items-center space-x-2 border border-blue-600 bg-white text-blue-600 hover:bg-blue-50"
                                    onClick={() => handleTestSync('MISSING_ENTITIES')}
                                >
                                    <Database className="w-4 h-4" />
                                    <span>Simulate Missing Entities</span>
                                </button>

                                <button
                                    type="button"
                                    className="px-4 py-2 rounded-md font-medium transition-all duration-200 flex items-center space-x-2 border border-blue-600 bg-white text-blue-600 hover:bg-blue-50"
                                    onClick={() => handleTestSync('MISSING_GROUP_ID')}
                                >
                                    <Database className="w-4 h-4" />
                                    <span>Simulate Schema Error</span>
                                </button>
                            </>
                        )}

                        {testCompleted && areAllStepsSuccessful() && (
                            <>
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center space-x-2 text-green-600">
                                        <CheckCircle className="w-4 h-4" />
                                        <span className="text-sm font-medium">Test Completed - Schema Verified</span>
                                    </div>
                                </div>
                            </>
                        )}

                    </div>

                    {/* Testing Steps */}
                    {renderTestingSteps()}

                    {/* Updated: Only show this button when test completes successfully AND no metadata scenarios */}
                    {testCompleted &&
                        areAllStepsSuccessful() &&
                        (!customerMetaDataKeys || customerMetaDataKeys.length === 0) && (
                            <button
                                type="button"
                                onClick={() => {
                                    
                                    setActiveStep('pause-fix');
                                    setTimeout(() => {
                                        step4NumberRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }, 100);
                                }}
                                className="flex px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium mt-4 ml-auto"
                            >
                                Proceed to Next Step
                            </button>
                        )}
                </div>
            </div>
        );
    };

    const renderIngestionLoadingSteps = () => {
        if (!showIngestionLoadingSteps) return null;
        return (
            <div className="mt-4 space-y-3 border border-gray-200 p-4 rounded-lg">
                {ingestionLoadingSteps.map((step, idx) => (
                    <div key={step.id} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1 text-lg">
                            {step.status === 'loading' && <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />}
                            {step.status === 'success' && <CheckCircle className="w-4 h-4 text-green-600" />}
                            {step.status === 'error' && <AlertTriangle className="w-4 h-4 text-red-600" />}
                            {step.status === 'pending' && (
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                            )}
                        </div>
                        <div className="flex-1">
                            <div className="font-medium text-gray-900 text-lg">
                                {step.status === 'loading' && step.loadingText}
                                {step.status === 'success' && step.successText}
                                {step.status === 'error' && step.errorText}
                                {step.status === 'pending' && step.loadingText}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };


    const renderPauseFixContent = () => {
        const isResyncFlow =
            (postSaveFlowMode === 'apiKeyOnly' ||
            postSaveFlowMode === 'both' ||
            postSaveFlowMode === 'syncDateOnly') &&
            step4WasEverCompleted;
        
        // Show button when step4 is not completed, regardless of whether it was completed before
        const shouldShowButton = !step4Completed;

        const handleStartIngestion = async () => {
            try {
                setIngestionError(null);
                if (!isResyncFlow) {
                    setShowIngestionLoadingSteps(true);
                    setIngestionStepIndex(0);
                    setIngestionLoadingSteps(ingestionLoadingStepsDefault);
                    setIngestionResult(null);
                }

                let response;

                if (postSaveFlowMode === 'apiKeyOnly') {
                    const res = await triggerSync({
                        variables: {
                            input: {
                                environmentId: formData.environmentId,
                                dataSource: DataSource.Chargebee
                            }
                        }
                    });

                    if (res.data?.triggerSync?.isSuccess) {
                        const nowIso = new Date().toISOString();
                        setStep4Completed(true);
                        setSyncStatus('running');
                        setIsSyncPolling(true);
                        setIsSetupIncomplete(false);
                        setResyncStarted(true);
                        
                        // Preserve existing ingestionResult and update sync status
                        const persistedIngestionResult = sourceConfigurations?.chargebee?.ingestionResult || ingestionResult;
                        const updatedIngestionResult = persistedIngestionResult ? {
                            ...persistedIngestionResult,
                            syncStatus: 'running',
                            syncStartedAt: nowIso
                        } : null;
                        setIngestionResult(updatedIngestionResult);

                        // Once step2 is completed (saved in config), it should remain true even if formData is incomplete
                        const step2ShouldBeCompleted = sourceConfigurations.chargebee?.step2Completed === true 
                            ? true 
                            : !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate);

                        const cfgUpdate = {
                            ...formData,
                            step1Completed: !!(formData.environmentId && formData.chargebeeEnvironment),
                            step2Completed: step2ShouldBeCompleted,
                            step3Completed: step3Completed,
                            step3CompletedAt: sourceConfigurations.chargebee?.step3CompletedAt || nowIso,
                            step4Completed: true,
                            step4WasEverCompleted: true,
                            step4CompletedAt: nowIso,
                            isStep3Incomplete: isStep3Incomplete,
                            testingSteps: testingSteps,
                            testCompleted: testCompleted,
                            schemaVerified: sourceConfigurations.chargebee?.schemaVerified || false,
                            selectedIdentifierKey: selectedAccountKey,
                            customerMetaDataKeys: customerMetaDataKeys,
                            groupIdFound: groupIdFound,
                            ingestionResult: updatedIngestionResult,
                            isSetupIncomplete: false,
                            pendingApiKeyChange: false,
                            pendingSyncDateChange: false
                        };
                        updateSourceConfiguration('chargebee', cfgUpdate);
                        setConfig(cfgUpdate);
                        onAutoSave();
                    } else {
                        setIngestionError('Failed to trigger resync');
                    }
                    return;
                }

                if (postSaveFlowMode === 'syncDateOnly' || postSaveFlowMode === 'both') {
                    const res = await resetAndResyncAirbyteData({
                        variables: {
                            input: {
                                environmentId: formData.environmentId,
                                sourceType: IntegrationType.Chargebee
                            }
                        }
                    });

                    if (res.data?.resetAndResyncAirbyteData?.isSuccess) {
                        const nowIso = new Date().toISOString();
                        setStep4Completed(true);
                        setSyncStatus('running');
                        setIsSyncPolling(true);
                        setIsSetupIncomplete(false);
                        setResyncStarted(true);
                        
                        // Preserve existing ingestionResult and update sync status
                        const persistedIngestionResult = sourceConfigurations?.chargebee?.ingestionResult || ingestionResult;
                        const updatedIngestionResult = persistedIngestionResult ? {
                            ...persistedIngestionResult,
                            syncStatus: 'running',
                            syncStartedAt: nowIso
                        } : null;
                        setIngestionResult(updatedIngestionResult);

                        // Once step2 is completed (saved in config), it should remain true even if formData is incomplete
                        const step2ShouldBeCompleted = sourceConfigurations.chargebee?.step2Completed === true 
                            ? true 
                            : !!(formData.sourceName && formData.subdomain && formData.apiKey && formData.syncStartDate);

                        const cfgUpdate = {
                            ...formData,
                            step1Completed: !!(formData.environmentId && formData.chargebeeEnvironment),
                            step2Completed: step2ShouldBeCompleted,
                            step3Completed: step3Completed,
                            step3CompletedAt: sourceConfigurations.chargebee?.step3CompletedAt || nowIso,
                            step4Completed: true,
                            step4WasEverCompleted: true,
                            step4CompletedAt: nowIso,
                            isStep3Incomplete: isStep3Incomplete,
                            testingSteps: testingSteps,
                            testCompleted: testCompleted,
                            schemaVerified: sourceConfigurations.chargebee?.schemaVerified || false,
                            selectedIdentifierKey: selectedAccountKey,
                            customerMetaDataKeys: customerMetaDataKeys,
                            groupIdFound: groupIdFound,
                            ingestionResult: updatedIngestionResult,
                            isSetupIncomplete: false,
                            pendingApiKeyChange: false,
                            pendingSyncDateChange: false
                        };
                        updateSourceConfiguration('chargebee', cfgUpdate);
                        setConfig(cfgUpdate);
                        onAutoSave();
                    } else {
                        setIngestionError(res.data?.resetAndResyncAirbyteData?.message || 'Failed to reset and resync');
                    }
                    return;
                }

                if (isDemoMode) {
                    response = await simulateCreateChargebeeIntegration({
                        environmentId: formData.environmentId,
                        chargebeeEnvironmentType: formData.chargebeeEnvironment,
                        chargebeeSubdomain: formData.subdomain,
                        chargebeeApiKey: formData.apiKey,
                        syncStartTime: formData.syncStartDate instanceof Date ? formData.syncStartDate.toISOString() : new Date().toISOString()
                    });
                } else {
                    const chargebeeEnvType = formData.chargebeeEnvironment === 'live'
                        ? ChargebeeEnvironmentType.Live
                        : ChargebeeEnvironmentType.Test;

                    const result = await createAirbyteIntegration({
                        variables: {
                            input: {
                                environmentId: formData.environmentId,
                                integrationType: IntegrationType.Chargebee,
                                syncStartTime: formData.syncStartDate instanceof Date ? formData.syncStartDate.toISOString().split('.')[0] + 'Z' : new Date().toISOString().split('.')[0] + 'Z',
                                chargebeeConfig: {
                                    siteName: formData.subdomain || "",
                                    apiKey: formData.apiKey,
                                    productCatalog: formData.productCatalog
                                }
                            }
                        }
                    });

                    if (result.data?.createAirbyteIntegration) {
                        response = result.data.createAirbyteIntegration;
                    }
                }

                    if (response) {
                    const config = {
                        ...formData,
                        status: 'configured',
                        completed: true,
                        completedAt: new Date().toISOString(),
                        step3Completed: step3Completed,
                        step3CompletedAt: step3Completed ? (sourceConfigurations.chargebee?.step3CompletedAt || new Date().toISOString()) : new Date().toISOString(),
                        step4Completed: true,
                        step4WasEverCompleted: true,
                        step4CompletedAt: new Date().toISOString(),
                        isStep3Incomplete: isStep3Incomplete,
                        testingSteps: testingSteps,
                        testCompleted: testCompleted,
                        schemaVerified: sourceConfigurations.chargebee?.schemaVerified || false,
                        selectedIdentifierKey: selectedAccountKey,
                        customerMetaDataKeys: customerMetaDataKeys,
                        groupIdFound: groupIdFound,
                        ingestionResult: {
                            sourceId: response.sourceId,
                            connectionId: response.connectionId,
                            syncStartedAt: response.syncStartedAt,
                            syncStatus: response.syncStatus,
                            jobId: response.jobId
                        }
                    };
                    updateSourceConfiguration('chargebee', config);
                    setStep4Completed(true);
                    onTrackCompletion?.();

                    setIngestionResult({
                        sourceId: response.sourceId,
                        connectionId: response.connectionId,
                        syncStartedAt: response.syncStartedAt,
                        syncStatus: response.syncStatus,
                        jobId: response.jobId
                    });

                    setSyncStatus('running');
                    setIsSyncPolling(true);
                    setIsSetupIncomplete(false);
                    
                    
                    if (response.syncStartedAt) {
                        setLastSyncedDate(response.syncStartedAt);
                    }

                    onAutoSave();

                    return response.syncStatus;
                }
            } catch (error) {
                console.error('Failed to start Chargebee ingestion:', error);
                setIngestionError(error instanceof Error ? error.message : 'Unknown error occurred');
            }
        };

        // Show status panel when step4 is completed OR during resync loading
        const showStatusPanel = (step4Completed && !!ingestionResult) || 
            (step4Completed && resyncStarted && (syncStatus === 'running' || syncStatus === 'pending')) ||
            (isResyncFlow && (triggerSyncLoading || resetAndResyncLoading));
        // Show button when step4 is not completed AND not currently loading a resync
        const showResyncAction = shouldShowButton && !triggerSyncLoading && !resetAndResyncLoading;

        return (
            <div className="space-y-6">
                {showStatusPanel && (
                    <div className={`rounded-lg p-4 ${syncStatus === 'succeeded' ? 'bg-green-50' :
                        syncStatus === 'failed' ? 'bg-red-50' :
                            'bg-blue-50'
                        }`}>
                        <div className="flex items-start space-x-3">
                            {syncStatus === 'running' && (
                                <Loader2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0 animate-spin" />
                            )}
                            {syncStatus === 'succeeded' && (
                                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                            )}
                            {syncStatus === 'failed' && (
                                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                            )}
                            {syncStatus === 'pending' && (
                                <Loader2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0 animate-spin" />
                            )}
                            <div className="flex-1">
                                <h4 className={`font-semibold text-xl mb-1 ${syncStatus === 'succeeded' ? 'text-green-900' :
                                    syncStatus === 'failed' ? 'text-red-900' :
                                        'text-blue-900'
                                    }`}>
                                    {syncStatus === 'running' || syncStatus === 'pending'
                                        ? 'Processing Ingestion'
                                        : syncStatus === 'succeeded'
                                            ? 'Ingestion Completed Successfully'
                                            : syncStatus === 'failed'
                                                ? 'Ingestion Failed'
                                                : 'Processing Ingestion'}
                                </h4>
                                <p className={`text-sm mb-2 ${syncStatus === 'succeeded' ? 'text-green-800' :
                                    syncStatus === 'failed' ? 'text-red-800' :
                                        'text-blue-800'
                                    }`}>
                                    {syncStatus === 'running' || syncStatus === 'pending'
                                        ? "We're currently syncing your Chargebee data. This process may take a 5-7 minutes. You'll receive an email notification once complete."
                                        : syncStatus === 'succeeded'
                                            ? 'Your Chargebee data has been successfully synced and is ready for analysis.'
                                            : syncStatus === 'failed'
                                                ? 'There was an error syncing your Chargebee data. Please try again or contact support.'
                                                : "We're currently syncing your Chargebee data. This process may take a 5-7 minutes. You'll receive an email notification once complete."}
                                </p>
                                <div className={`text-xs ${syncStatus === 'succeeded' ? 'text-green-700' :
                                    syncStatus === 'failed' ? 'text-red-700' :
                                        'text-blue-700'
                                    }`}>
                                    {lastSyncedDate && (
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium">Last synced:</span>
                                            <span>{new Date(lastSyncedDate).toLocaleString('en-US', {
                                                weekday: 'short',
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit'
                                            })}</span>
                                            {isSyncPolling && (
                                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" title="Checking status every 30 seconds"></div>
                                            )}
                                        </div>
                                    )}
                                    {!lastSyncedDate && ingestionResult?.syncStartedAt && (
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium">Sync started at:</span>
                                            <span>{new Date(ingestionResult.syncStartedAt).toLocaleString('en-US', {
                                                weekday: 'short',
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit'
                                            })}</span>
                                            {isSyncPolling && (
                                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" title="Checking status every 30 seconds"></div>
                                            )}
                                        </div>
                                    )}
                                    <div className="mt-1">
                                        <span className="font-medium">Status:</span>{' '}
                                        <span className={`font-semibold ${
                                            syncStatus === 'succeeded' ? 'text-green-600' :
                                            syncStatus === 'failed' ? 'text-red-600' :
                                            syncStatus === 'running' || syncStatus === 'pending' ? 'text-blue-600' :
                                            'text-gray-600'
                                        }`}>
                                            {syncStatus === 'succeeded' ? 'Completed' :
                                             syncStatus === 'failed' ? 'Failed' :
                                             syncStatus === 'running' || syncStatus === 'pending' ? 'In Progress' :
                                             'Pending'}
                                        </span>
                                    </div>
                                    {syncStatus === 'failed' && (
                                        <div className="mt-1">
                                            <span className="font-medium">Status:</span> <span className="text-red-600 font-semibold">Failed</span>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-6">
                                    <p className="text-sm text-blue-800 mb-3">
                                        Notification will be sent to: <p className="font-semibold">{`${getUser()?.email}`}</p>
                                    </p>

                                    {!emailManager.state.showInput ? (
                                        <button
                                            type="button"
                                            onClick={emailManager.toggleInput}
                                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                                        >
                                            <UserPlus className="w-4 h-4" />
                                            Add Additional Emails
                                        </button>
                                    ) : (
                                        <div className="space-y-2">
                                            <label className="block text-sm text-blue-900">
                                                Add additional email addresses where you’d like to receive notifications.
                                            </label>



                                            {/* Email input */}
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={emailManager.state.inputValue}
                                                    onChange={(e) => emailManager.handleInputChange(e.target.value)}
                                                    onKeyDown={emailManager.handleKeyDown}
                                                    placeholder="Enter email and press Enter or comma"
                                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${emailManager.state.error ? 'border-red-500' : 'border-gray-300'
                                                        }`}
                                                />
                                                {emailManager.state.error && (
                                                    <p className="text-red-600 text-xs mt-1">{emailManager.state.error}</p>
                                                )}
                                            </div>
                                            {/* Email chips display */}
                                            {emailManager.state.emails.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    {emailManager.state.emails.map((email, index) => (
                                                        <div
                                                            key={index}
                                                            className="flex items-center gap-1 px-3 py-1 bg-gray-200 rounded-full text-sm"
                                                        >
                                                            <span>{email}</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => emailManager.removeEmail(email)}
                                                                className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Update button */}
                                            {emailManager.state.showUpdateButton && (
                                                <button
                                                    type="button"
                                                    onClick={handleUpdateNotificationEmails}
                                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
                                                >
                                                    Update Notification Emails
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showResyncAction && (
                    <>
                        <p>
                            {postSaveFlowMode === 'syncDateOnly'
                                ? 'Start a new sync with modified configurations.'
                                : (postSaveFlowMode === 'apiKeyOnly' || postSaveFlowMode === 'both')
                                    ? 'Start a new sync with modified configurations.'
                                    : 'Start ingestion of your data from Chargebee by starting the data sync. First time syncing of all data will take a while to populate the revenue analytics dashboard.'}
                        </p>

                        <button
                            type="button"
                            onClick={handleStartIngestion}
                            disabled={
                                (postSaveFlowMode === 'apiKeyOnly')
                                    ? (!step3Completed || triggerSyncLoading || updateAirbyteSourceLoading)
                                    : (postSaveFlowMode === 'syncDateOnly' || postSaveFlowMode === 'both')
                                        ? (!step3Completed || resetAndResyncLoading || updateAirbyteSourceLoading)
                                        : (!testCompleted || createIntegrationLoading)
                            }
                            className="px-6 py-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                            {(createIntegrationLoading || triggerSyncLoading || resetAndResyncLoading) && <Loader2 className="w-4 h-4 animate-spin" />}
                            <span>
                                {isResyncFlow && (postSaveFlowMode === 'apiKeyOnly' || postSaveFlowMode === 'both')
                                    ? (triggerSyncLoading ? 'Starting Resync...' : 'Resync Chargebee data')
                                    : isResyncFlow && (postSaveFlowMode === 'syncDateOnly')
                                        ? (resetAndResyncLoading ? 'Starting Resync...' : 'Resync Chargebee data')
                                        : (createIntegrationLoading ? 'Starting Ingestion...' : 'Start Ingestion')}
                            </span>
                        </button>

                        {isStep3Incomplete && <p><strong>Note:</strong> You can start Data Ingestion now and see revenue analytics data and fix the schema for data correlation later. </p>}
                    </>
                )}
                {/* Error Display */}
                {ingestionError && (
                    <div className="bg-red-50 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <h4 className="text-sm font-semibold text-red-900 mb-1">
                                    Failed to Start Ingestion
                                </h4>
                                <p className="text-sm text-red-800">Something went wrong, Please try again.</p>
                            </div>
                        </div>
                    </div>
                )}
                {!isResyncFlow && <div>{renderIngestionLoadingSteps()}</div>}
                {showStatusPanel && (
                    <div className="mt-4 ml-6 space-y-4">
                        <div>You can continue to <strong>Core Revenue Calculation</strong> to set up currency and other fee-related configurations while the sync completes in background.</div>
                    </div>
                )}
            </div>
        );
    };

    const renderStepContent = (step: StepConfig) => {
        switch (step.id) {
            case 'environment':
                return renderEnvironmentContent(step);
            case 'basic-config':
                return renderBasicConfigContent();
            case 'revenue-correlation':
                return renderRevenueCorrelationContent();
            case 'pause-fix':
                return renderPauseFixContent();
            default:
                return null;
        }
    };

    return (
        <div className="bg-white">
            <div className="container mt-8">
                <div className="">
                    <div className="mx-auto w-full bg-white rounded-lg">
                        <form onSubmit={onSubmit} className="space-y-8">
                            {/* Steps Container */}
                            <div className="relative">
                                {/* Connector line */}
                                <div
                                    className="absolute left-5 w-0.5 bg-gray-300"
                                    style={getConnectorLineStyle()}
                                />

                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative z-10 ml-10">
                                    {stepsConfig.map((step, idx) => (
                                        <div
                                            key={step.id}
                                            ref={step.id === 'basic-config' ? step2ContainerRef : step.id === 'revenue-correlation' ? step3ContainerRef : step.id === 'pause-fix' ? step4ContainerRef : null}
                                            className={`border-b border-gray-200 last:border-b-0 transition-all duration-300 ease-in-out ${step.isActive ? 'bg-blue-50' : ''
                                                }`}
                                        >
                                            {(() => {
                                                const step3IsCompleteForGating = step3Completed;
                                                const baseLocked =
                                                    (step.id === 'revenue-correlation' || step.id === 'pause-fix') &&
                                                    (!isBasicConfigStepCompleted || isConfigEditPendingBigSave);
                                                const lockedByStep3 =
                                                    requiresStep3CompletionForResync &&
                                                    step.id === 'pause-fix' &&
                                                    !step3IsCompleteForGating;
                                                const isLockedStep = baseLocked || lockedByStep3;
                                                return (
                                                    <div className={`relative ${isLockedStep ? 'group' : ''}`}>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                if (isLockedStep) return;
                                                                setIsManualStepChange(true);
                                                                const nextStep = step.isActive ? '' : step.id;
                                                                if (nextStep) userChosenStepRef.current = nextStep;
                                                                setActiveStep(nextStep);
                                                            }}
                                                            aria-disabled={isLockedStep}
                                                            className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
                                                                isLockedStep ? 'cursor-not-allowed opacity-70' : 'hover:bg-gray-50'
                                                            }`}
                                                        >
                                                <div className="flex items-center gap-4 flex-1">
                                                    {/* Step numbers */}
                                                    <div
                                                        ref={step.id === 'environment' ? step1NumberRef : step.id === 'basic-config' ? step2NumberRef : step.id === 'revenue-correlation' ? step3NumberRef : step4NumberRef}
                                                        className={`absolute -left-10 w-8 h-8 flex items-center justify-center rounded-full font-semibold text-sm z-10 border-2 transition-all duration-300 ${step.isCompleted
                                                            ? 'border-green-500'
                                                            : step.isActive
                                                                ? 'bg-blue-500 text-white border-blue-500'
                                                                : 'bg-gray-100 text-gray-600 border-gray-300'
                                                            }`}
                                                        style={step.isCompleted ? {
                                                            backgroundColor: 'rgb(34, 197, 94)',
                                                            color: 'rgb(255, 255, 255)'
                                                        } : {}}
                                                    >
                                                        {step.number}
                                                    </div>

                                                    <div className="flex-1">
                                                        <div className="flex items-center justify-between gap-4">
                                                            {/* Left: Title and Required Indicator */}
                                                            <div className="flex items-center gap-2">
                                                                <div className="font-semibold text-gray-900 text-lg">{step.title}</div>
                                                                {step.required && !isStep3Incomplete && (
                                                                    <span className="text-red-800 text-lg">*</span>
                                                                )}
                                                            </div>

                                                            {/* Environment connection diagram */}
                                                            {step.id === 'environment' && step.isCompleted && (
                                                                <div className="flex items-center ml-10">{renderEnvironmentDiagram(testCompleted)}</div>
                                                            )}

                                                            {/* Middle + Right: Diagram and Status Badges grouped together */}
                                                            <div className="flex items-center gap-4">
                                                                {/* Status badges */}
                                                                {step.isCompleted && (
                                                                    <div className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                                                        Completed
                                                                    </div>
                                                                )}

                                                                {step.id === 'revenue-correlation' && isStep3Incomplete && (
                                                                    <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 h-5">
                                                                        <AlertTriangle className="w-3 h-3" />
                                                                        <span>Action Required</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {step.id === 'environment' ? step.subtitle : step.description}
                                                        </div>
                                                    </div>
                                                </div>

                                                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ml-2 ${step.isActive ? 'rotate-180' : ''
                                                    }`} />
                                                        </button>
                                                        {isLockedStep && (
                                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20">
                                                                {lockedByStep3 ? 'Complete Step 3 to proceed.' : 'Complete Basic Configurations Step.'}
                                                                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            {step.isActive && (
                                                <div className="border-t border-gray-200 p-6 bg-white transition-all duration-300 ease-in-out">
                                                    {renderStepContent(step)}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Setup Guide Modal */}
            {isSetupGuideOpen && (
                <div className="fixed inset-0 z-50 overflow-hidden">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsSetupGuideOpen(false)} />
                    <div className="absolute right-0 top-0 h-full w-[600px] bg-white shadow-xl overflow-y-auto">
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold">Setup Guide: Chargebee</h2>
                                <button
                                    onClick={() => setIsSetupGuideOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <ChargebeeSetupGuide step3Ref={setupGuideStep3Ref} setExpanded={setExpanded} expanded={expanded} />
                        </div>
                    </div>
                </div>
            )}

            <GroupIdMappingModal
                open={showMappingModal}
                onOpenChange={setShowMappingModal}
                onProceed={handleProceedAnyway}
            />

            {showInviteModal && (
                <InviteTeamModal
                    isOpen={showInviteModal}
                    onClose={() => setShowInviteModal(false)}
                    team={selectedTeam}
                    onInvite={() => { }}
                    source={selectedSources === 'chargebee' ? selectedSources : undefined}
                />
            )}

            {/* Fix Instructions Modal */}
            <FixInstructionsModal
                isOpen={showFixInstructionsModal}
                onClose={() => setShowFixInstructionsModal(false)}
                onInviteTeam={handleInviteTeam}
                source="chargebee"
                accountIdentifierKey={selectedAccountKey}
            />


            {/* Environment Modal */}
            {showNewEnvironmentForm && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-20">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Create New Environment</h3>
                            <button
                                onClick={handleCancelNewEnvironment}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Environment Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newEnvironmentForm.displayName}
                                    onChange={(e) => setNewEnvironmentForm(prev => ({ ...prev, displayName: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter environment name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Environment Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={newEnvironmentForm.environmentType}
                                    onChange={(e) => setNewEnvironmentForm(prev => ({ ...prev, environmentType: e.target.value as EnvironmentType }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value={EnvironmentType.Production}>Production</option>
                                    <option value={EnvironmentType.NonProduction}>NonProduction</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                            <button
                                onClick={handleCancelNewEnvironment}
                                className="px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateNewEnvironment}
                                disabled={!newEnvironmentForm.displayName || createEnvironmentLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                            >
                                {createEnvironmentLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                                <span>{createEnvironmentLoading ? 'Creating...' : 'Create Environment'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
